import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Download, LogOut, Save, Users, Settings, Calendar as CalendarIcon, Video, Code, Target, MessageCircle, Link, Copy, CalendarPlus, Trash2, Eye, Pencil, Send, X, Clock, Bell, BarChart3, FileSpreadsheet, ChevronDown, ChevronUp, CopyPlus, FileSignature } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [zoomLink, setZoomLink] = useState('');
  const [watiTemplate, setWatiTemplate] = useState('');
  const [watiTemplates, setWatiTemplates] = useState([]);
  const [trackingPixel, setTrackingPixel] = useState('');
  const [sgdRate, setSgdRate] = useState(3.20);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [campaigns, setCampaigns] = useState({});
  const [totalRegs, setTotalRegs] = useState(0);
  const [activeCampaign, setActiveCampaign] = useState('all');
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeTab, setActiveTab] = useState('registrations');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ name: '', slug: '', date: '', time: '', description: '' });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventBookings, setEventBookings] = useState([]);
  const [eventCopied, setEventCopied] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [waMessages, setWaMessages] = useState({ message1: '', message2: '', message3: '', message4: '' });
  const [editingWaMsg, setEditingWaMsg] = useState(null);
  const [waMsgDraft, setWaMsgDraft] = useState('');
  const [waSaved, setWaSaved] = useState(false);
  const [slots, setSlots] = useState([]);
  const [newSlotDate, setNewSlotDate] = useState('');
  const [newSlotDayLabel, setNewSlotDayLabel] = useState('');
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [addingSlots, setAddingSlots] = useState(false);
  const [applications, setApplications] = useState([]);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [interviewData, setInterviewData] = useState({});
  const [interviewSaving, setInterviewSaving] = useState(false);
  const [interviewSaved, setInterviewSaved] = useState(null);
  const [formSlug, setFormSlug] = useState('');
  const [formSlugSaved, setFormSlugSaved] = useState(false);
  const [sigSlug, setSigSlug] = useState('');
  const [sigSlugSaved, setSigSlugSaved] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [sheetUrl, setSheetUrl] = useState('');
  const [importingSheet, setImportingSheet] = useState(false);
  const [importedSheets, setImportedSheets] = useState([]);
  const [aggregate, setAggregate] = useState(null);
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [sheetDetails, setSheetDetails] = useState({});
  const [expandedApps, setExpandedApps] = useState(false);
  const [editingPhone, setEditingPhone] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [editingName, setEditingName] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [slotInfoId, setSlotInfoId] = useState(null);
  const [slotsCollapsed, setSlotsCollapsed] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [refreshCountdown, setRefreshCountdown] = useState(120);
  const [reminders, setReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [reminderChecked, setReminderChecked] = useState(false);
  const [sigPending, setSigPending] = useState([]);
  const [sigPendingTotal, setSigPendingTotal] = useState(0);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, regsRes, watiRes] = await Promise.all([
        fetch(`${API_URL}/api/settings`),
        fetch(`${API_URL}/api/admin/registrations`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/wati-templates`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const settings = await settingsRes.json();
      setDate(settings.date);
      setTime(settings.time);
      setZoomLink(settings.zoom_link || '');
      setWatiTemplate(settings.wati_template || '');
      setTrackingPixel(settings.tracking_pixel || '');
      setSgdRate(settings.sgd_rate || 3.20);
      if (regsRes.ok) {
        const regsData = await regsRes.json();
        setRegistrations(regsData.registrations);
        setTotalRegs(regsData.total);
        setCampaigns(regsData.campaigns || {});
      }
      if (watiRes.ok) {
        const watiData = await watiRes.json();
        setWatiTemplates(watiData.templates || []);
      }
      // Fetch events
      const [eventsRes, waRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/events`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/wa-messages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData.events || []);
      }
      if (waRes.ok) {
        const waData = await waRes.json();
        setWaMessages(waData);
      }
    } catch {
      navigate('/admin');
    }
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    fetchData();
    fetchSigPending();
    // Fetch reminders on login
    fetch(`${API_URL}/api/admin/reminders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { reminders: [] })
      .then(data => {
        setReminders(data.reminders || []);
        if (data.reminders && data.reminders.length > 0) setShowReminders(true);
        setReminderChecked(true);
      });
  }, [token, navigate, fetchData]);

  const fetchReminders = async () => {
    const res = await fetch(`${API_URL}/api/admin/reminders`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const data = await res.json(); setReminders(data.reminders || []); }
  };

  const addReminder = async (bookingId, bookingName) => {
    const message = prompt(`Set reminder for "${bookingName}":\n(e.g. Signature due by 25th Aug)`);
    if (!message) return;
    const dueDate = prompt('Due date (YYYY-MM-DD):');
    if (!dueDate) return;
    await fetch(`${API_URL}/api/admin/reminders`, {
      method: 'POST', headers,
      body: JSON.stringify({ booking_id: bookingId, booking_name: bookingName, message, due_date: dueDate })
    });
    fetchReminders();
  };

  const markReminderDone = async (id) => {
    await fetch(`${API_URL}/api/admin/reminders/${id}/done`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const dismissReminder = async (id) => {
    await fetch(`${API_URL}/api/admin/reminders/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setReminders(prev => prev.filter(r => r.id !== id));
  };

  const fetchSigPending = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/signature-pending`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSigPending(data.pending || []);
        setSigPendingTotal(data.total || 0);
      }
    } catch { /* ignore */ }
  }, [token]);

  const markSigDone = async (bookingId) => {
    await fetch(`${API_URL}/api/admin/signature-pending/${bookingId}/done`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
    setSigPending(prev => prev.filter(p => p.id !== bookingId));
    setSigPendingTotal(prev => Math.max(0, prev - 1));
  };

  const goToBooking = async (eventSlug, bookingId) => {
    setActiveTab('events');
    await viewBookings(eventSlug);
    setExpandedBooking(bookingId);
    setTimeout(() => {
      const row = document.getElementById(`booking-row-${bookingId}`);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  };

  useEffect(() => {
    if (activeTab === 'analytics' && token) fetchSheets();
    if (activeTab === 'signature' && token) fetchSigPending();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'events' || !selectedEvent || !token) return;
    setRefreshCountdown(120);
    const tick = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) {
          viewBookings(selectedEvent, true);
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [activeTab, selectedEvent, token]);

  const saveSettings = async () => {
    const res = await fetch(`${API_URL}/api/admin/settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ date, time, zoom_link: zoomLink, wati_template: watiTemplate, tracking_pixel: trackingPixel, sgd_rate: parseFloat(sgdRate) || 3.20 })
    });
    if (res.ok) {
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    }
  };

  const downloadCSV = async () => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${API_URL}/api/admin/registrations/download${qs}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'registrations.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const createEvent = async () => {
    if (!newEvent.name || !newEvent.slug) return;
    const res = await fetch(`${API_URL}/api/admin/events`, {
      method: 'POST', headers,
      body: JSON.stringify(newEvent)
    });
    if (res.ok) {
      setNewEvent({ name: '', slug: '', date: '', time: '', description: '' });
      fetchData();
    } else {
      const err = await res.json();
      alert(err.detail || 'Error creating event');
    }
  };

  const deleteEvent = async (slug) => {
    if (!window.confirm('Delete this event?')) return;
    await fetch(`${API_URL}/api/admin/events/${slug}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setSelectedEvent(null);
    setEventBookings([]);
    fetchData();
  };

  const cloneEvent = async (slug) => {
    const res = await fetch(`${API_URL}/api/admin/events/${slug}/clone`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      alert(`Event cloned as "${data.event.name}" with ${data.slots_cloned} slots.`);
      fetchData();
    } else {
      const err = await res.json();
      alert(err.detail || 'Failed to clone event');
    }
  };

  // Long-press to create event from campaign
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  const createEventFromCampaign = async (campaignName) => {
    const campaignRegs = campaigns[campaignName] || [];
    if (!window.confirm(`Create event "${campaignName}" and import ${campaignRegs.length} registrations as bookings?`)) return;
    const slug = campaignName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const res = await fetch(`${API_URL}/api/admin/events`, {
      method: 'POST', headers,
      body: JSON.stringify({ name: campaignName, slug, date: campaignName, time: '', description: '' })
    });
    if (res.ok) {
      // Import all campaign registrations as event bookings
      let imported = 0, skipped = 0;
      for (const reg of campaignRegs) {
        const bookRes = await fetch(`${API_URL}/api/events/${slug}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: reg.name || '', phone: reg.phone || '', email: reg.email || '', gender: '' })
        });
        if (bookRes.ok) imported++;
        else skipped++;
      }
      alert(`Event created with ${imported} bookings imported${skipped > 0 ? ` (${skipped} skipped)` : ''}.`);
      fetchData();
      setActiveTab('events');
    } else {
      const err = await res.json();
      alert(err.detail || 'Error creating event');
    }
  };

  const startLongPress = (campaignName) => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      createEventFromCampaign(campaignName);
    }, 600);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const viewBookings = async (slug, isAutoRefresh = false) => {
    setSelectedEvent(slug);
    if (!isAutoRefresh) {
      setSlotsCollapsed(false);
    }
    const [bookingsRes, slotsRes, appsRes, sigsRes] = await Promise.all([
      fetch(`${API_URL}/api/admin/events/${slug}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_URL}/api/admin/slots/all`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_URL}/api/admin/events/${slug}/applications`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_URL}/api/admin/events/${slug}/signatures`, { headers: { Authorization: `Bearer ${token}` } })
    ]);
    if (bookingsRes.ok) {
      const data = await bookingsRes.json();
      setEventBookings(data.bookings || []);
    }
    if (slotsRes.ok) {
      const data = await slotsRes.json();
      setSlots(data.slots || []);
    }
    if (appsRes.ok) {
      const data = await appsRes.json();
      setApplications(data.applications || []);
    }
    if (sigsRes.ok) {
      const data = await sigsRes.json();
      setSignatures(data.signatures || []);
    }
    if (!isAutoRefresh) {
      setExpandedBooking(null);
    }
    // Set form slug from event data
    const ev = events.find(e => e.slug === slug);
    setFormSlug(ev?.form_slug || slug);
    setSigSlug(ev?.signature_slug || slug);
  };

  const downloadEventCSV = async (slug) => {
    const res = await fetch(`${API_URL}/api/admin/events/${slug}/bookings/download`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}_bookings.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const saveEvent = async () => {
    if (!editingEvent) return;
    const res = await fetch(`${API_URL}/api/admin/events/${editingEvent.slug}`, {
      method: 'PUT', headers,
      body: JSON.stringify(editingEvent)
    });
    if (res.ok) {
      setEditingEvent(null);
      fetchData();
    }
  };

  const saveWaMessage = async (msgKey, text) => {
    const updated = { ...waMessages, [msgKey]: text };
    const res = await fetch(`${API_URL}/api/admin/wa-messages`, {
      method: 'PUT', headers,
      body: JSON.stringify(updated)
    });
    if (res.ok) {
      setWaMessages(updated);
      setEditingWaMsg(null);
      setWaMsgDraft('');
      setWaSaved(true);
      setTimeout(() => setWaSaved(false), 2000);
    }
  };

  const findBookedSlot = (name, email, phone) => {
    const cleanP = phone.replace(/[\s\-()]/g, '');
    const lowerName = name.toLowerCase().trim();
    const lowerEmail = email.toLowerCase().trim();
    return slots.find(s => {
      if (!s.booked_by) return false;
      const bp = s.booked_by.phone.replace(/[\s\-()]/g, '');
      const bn = (s.booked_by.name || '').toLowerCase().trim();
      const be = (s.booked_by.email || '').toLowerCase().trim();
      return bp === cleanP || (lowerEmail && be === lowerEmail) || (lowerName && bn === lowerName);
    });
  };

  const findApplication = (name, email, phone) => {
    const cleanP = phone.replace(/[\s\-()]/g, '');
    const lowerName = name.toLowerCase().trim();
    const lowerEmail = email.toLowerCase().trim();
    return applications.find(a => {
      const ap = (a.phone || '').replace(/[\s\-()]/g, '');
      const an = (a.name || '').toLowerCase().trim();
      const ae = (a.email || '').toLowerCase().trim();
      return ap === cleanP || (lowerEmail && ae === lowerEmail) || (lowerName && an === lowerName);
    });
  };

  const findSignature = (name, email, phone) => {
    const cleanP = phone.replace(/[\s\-()]/g, '');
    const lowerName = name.toLowerCase().trim();
    const lowerEmail = email.toLowerCase().trim();
    return signatures.find(s => {
      const sp = (s.phone || '').replace(/[\s\-()]/g, '');
      const sn = (s.name || '').toLowerCase().trim();
      const se = (s.email || '').toLowerCase().trim();
      return sp === cleanP || (lowerEmail && se === lowerEmail) || (lowerName && sn === lowerName);
    });
  };

  const openWhatsApp = async (phone, message, eventSlug, booking, msgKey) => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '').replace(/^\+/, '');
    // Get short token for client identity
    let tokenParam = '';
    try {
      const res = await fetch(`${API_URL}/api/tokens`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: booking.name, phone: booking.phone, email: booking.email })
      });
      if (res.ok) {
        const data = await res.json();
        tokenParam = `?t=${data.token}`;
      }
    } catch {}
    const bookingUrl = `${window.location.origin}/book/${eventSlug}${tokenParam}`;
    const applicationUrl = `${window.location.origin}/apply/${formSlug || eventSlug}${tokenParam}`;
    const signatureUrl = `${window.location.origin}/signature/${sigSlug || eventSlug}${tokenParam}`;
    const bookedSlot = findBookedSlot(booking.name, booking.email, booking.phone);
    let finalMsg = message
      .replace(/\{booking_link\}/g, bookingUrl)
      .replace(/\{application_form_link\}/g, applicationUrl)
      .replace(/\{signature_link\}/g, signatureUrl)
      .replace(/\{name\}/g, booking.name || '')
      .replace(/\{booked_date\}/g, bookedSlot ? bookedSlot.day_label : '')
      .replace(/\{booked_time\}/g, bookedSlot ? bookedSlot.time : '');
    const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(finalMsg)}`;
    window.open(url, '_blank');
    // Track WA sent
    if (msgKey && booking.id) {
      const num = msgKey.replace('message', '');
      try {
        await fetch(`${API_URL}/api/admin/events/${eventSlug}/bookings/${booking.id}/wa-sent/${num}`, {
          method: 'PUT', headers: { Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sent: true })
        });
        setEventBookings(prev => prev.map(b => b.id === booking.id ? { ...b, [`wa${num}_sent`]: true } : b));
      } catch {}
    }
  };

  const toggleWaSent = async (booking, num) => {
    if (!selectedEvent || !booking.id) return;
    const field = `wa${num}_sent`;
    const newVal = !booking[field];
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings/${booking.id}/wa-sent/${num}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ sent: newVal })
    });
    if (res.ok) {
      setEventBookings(prev => prev.map(b => b.id === booking.id ? { ...b, [field]: newVal } : b));
    }
  };

  const NOTES_TEMPLATE = `Payment mode : \nDetail : \nWishes :\n- Signature of Wealth\nCycle - \nVideo Updated :\nLanguage : \n8-10 weeks \nNo uturn\nNo Instagram signature`;

  const INTERVIEWER_PHONE = '60122493838';

  const parseSlotDateTime = (slot) => {
    if (!slot || !slot.date) return Infinity;
    const timeStr = slot.time || '';
    const match = timeStr.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
    let hours = 0, mins = 0;
    if (match) {
      hours = parseInt(match[1]);
      mins = parseInt(match[2]);
      const ampm = match[3].toUpperCase();
      if (ampm === 'PM' && hours !== 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
    }
    return new Date(`${slot.date}T${String(hours).padStart(2,'0')}:${String(mins).padStart(2,'0')}:00`).getTime();
  };

  const getSortedBookings = () => {
    const now = Date.now();
    return [...eventBookings].map(b => {
      const slot = findBookedSlot(b.name, b.email, b.phone);
      const ts = slot ? parseSlotDateTime(slot) : Infinity;
      return { ...b, _slot: slot, _ts: ts };
    }).sort((a, b) => {
      const aHasSlot = a._ts !== Infinity;
      const bHasSlot = b._ts !== Infinity;
      if (aHasSlot && !bHasSlot) return -1;
      if (!aHasSlot && bHasSlot) return 1;
      if (!aHasSlot && !bHasSlot) return 0;
      // Both have slots: upcoming first (closest future), then past
      const aFuture = a._ts >= now;
      const bFuture = b._ts >= now;
      if (aFuture && !bFuture) return -1;
      if (!aFuture && bFuture) return 1;
      if (aFuture && bFuture) return a._ts - b._ts; // nearest future first
      return b._ts - a._ts; // most recent past first
    });
  };

  const sendReminder = (booking, slot) => {
    const clientName = booking.name || 'Client';
    const bookedTime = slot ? `${slot.day_label}, ${slot.time}` : '';
    const message = `You have a consultation coming up with ${clientName} at ${bookedTime}.`;
    const url = `https://api.whatsapp.com/send?phone=${INTERVIEWER_PHONE}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const getInterview = (bookingId, existing) => {
    if (interviewData[bookingId]) return interviewData[bookingId];
    if (existing) return existing;
    return { interviewed: '', status: '', program: '', payment_method: '', payment_amount: '', notes: NOTES_TEMPLATE, language: '' };
  };

  const setInterviewField = (bookingId, field, value, existing) => {
    const current = getInterview(bookingId, existing);
    setInterviewData(prev => ({ ...prev, [bookingId]: { ...current, [field]: value } }));
  };

  const saveInterview = async (bookingId) => {
    if (!selectedEvent) return;
    // Use local edits if available, otherwise use current server data
    const booking = eventBookings.find(b => b.id === bookingId);
    const data = interviewData[bookingId] || booking?.interview;
    if (!data) return;
    setInterviewSaving(true);
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings/${bookingId}/interview`, {
      method: 'PUT', headers,
      body: JSON.stringify(data)
    });
    if (res.ok) {
      setInterviewSaved(bookingId);
      setTimeout(() => setInterviewSaved(null), 2000);
      setEventBookings(prev => prev.map(b => b.id === bookingId ? { ...b, interview: data } : b));
    } else {
      alert('Failed to save interview data. Please try again.');
    }
    setInterviewSaving(false);
  };

  const savePhone = async (bookingId, newPhone) => {
    if (!selectedEvent || !newPhone.trim()) return;
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings/${bookingId}/phone`, {
      method: 'PUT', headers,
      body: JSON.stringify({ phone: newPhone.trim() })
    });
    if (res.ok) {
      setEventBookings(prev => prev.map(b => b.id === bookingId ? { ...b, phone: newPhone.trim() } : b));
      setEditingPhone(null);
    }
  };

  const saveName = async (bookingId, newName) => {
    if (!selectedEvent || !newName.trim()) return;
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings/${bookingId}/name`, {
      method: 'PUT', headers,
      body: JSON.stringify({ name: newName.trim() })
    });
    if (res.ok) {
      setEventBookings(prev => prev.map(b => b.id === bookingId ? { ...b, name: newName.trim() } : b));
      setEditingName(null);
    }
  };

  const deleteBooking = async (booking) => {
    if (!selectedEvent || !booking.id) return;
    if (!window.confirm(`Are you sure you want to remove "${booking.name}" from the list? This will also free their booked slot and remove related application/interview data.`)) return;
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings/${booking.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      setEventBookings(prev => prev.filter(b => b.id !== booking.id));
      fetchSlots();
    }
  };

  const deleteSelectedBookings = async () => {
    if (selectedBookings.size === 0) return;
    if (!window.confirm(`Delete ${selectedBookings.size} selected booking(s)? This will free their slots and remove related data.`)) return;
    const ids = [...selectedBookings];
    await Promise.all(ids.map(id =>
      fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      })
    ));
    setEventBookings(prev => prev.filter(b => !selectedBookings.has(b.id)));
    setSelectedBookings(new Set());
    setSelectMode(false);
    fetchSlots();
  };

  const toggleBookingSelection = (id) => {
    setSelectedBookings(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBookings.size === eventBookings.length) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(eventBookings.map(b => b.id)));
    }
  };

  const handleCsvUpload = async (e, replace = false) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEvent) return;
    
    if (replace && eventBookings.length > 0) {
      if (!window.confirm(`This will remove all ${eventBookings.length} existing entries and replace with the new list. Continue?`)) {
        e.target.value = '';
        return;
      }
    }
    
    setUploadingCsv(true);
    
    // If replace mode, clear existing bookings first
    if (replace) {
      await fetch(`${API_URL}/api/admin/events/${selectedEvent}/bookings`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
    }
    
    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length < 2) { alert('CSV must have a header row and at least one data row.'); setUploadingCsv(false); return; }
    
    const sep = lines[0].includes('\t') ? '\t' : ',';
    const hdrs = lines[0].split(sep).map(h => h.replace(/"/g, '').trim().toLowerCase());
    const nameIdx = hdrs.findIndex(h => h === 'name' || h === '#' ? false : h.includes('name'));
    const emailIdx = hdrs.findIndex(h => h.includes('email'));
    const phoneIdx = hdrs.findIndex(h => h.includes('phone'));

    if (nameIdx === -1) { alert('CSV must have a "Name" column.'); setUploadingCsv(false); return; }

    let imported = 0, skipped = 0;
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(sep).map(c => c.replace(/"/g, '').trim());
      const name = cols[nameIdx] || '';
      const email = emailIdx >= 0 ? cols[emailIdx] || '' : '';
      const phone = phoneIdx >= 0 ? cols[phoneIdx] || '' : '';
      if (!name) continue;
      
      const res = await fetch(`${API_URL}/api/events/${selectedEvent}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, email, gender: '' })
      });
      if (res.ok) imported++;
      else skipped++;
    }
    
    alert(`${replace ? 'Replace' : 'Import'} complete: ${imported} added${skipped > 0 ? `, ${skipped} skipped (duplicates)` : ''}.`);
    viewBookings(selectedEvent);
    if (replace) fetchSlots();
    setUploadingCsv(false);
    e.target.value = '';
  };

  const saveFormSlug = async () => {
    if (!selectedEvent || !formSlug.trim()) return;
    const cleanSlug = formSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/form-slug`, {
      method: 'PUT', headers,
      body: JSON.stringify({ form_slug: cleanSlug })
    });
    if (res.ok) {
      setFormSlug(cleanSlug);
      setFormSlugSaved(true);
      setTimeout(() => setFormSlugSaved(false), 2000);
      fetchData();
    }
  };

  const saveSigSlug = async () => {
    if (!selectedEvent || !sigSlug.trim()) return;
    const cleanSlug = sigSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/signature-slug`, {
      method: 'PUT', headers,
      body: JSON.stringify({ signature_slug: cleanSlug })
    });
    if (res.ok) {
      setSigSlug(cleanSlug);
      setSigSlugSaved(true);
      setTimeout(() => setSigSlugSaved(false), 2000);
      fetchData();
    }
  };

  const fetchSheets = async () => {
    try {
      const [sheetsRes, aggRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/sheets`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/sheets/aggregate`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (sheetsRes.ok) setImportedSheets((await sheetsRes.json()).sheets || []);
      if (aggRes.ok) setAggregate(await aggRes.json());
    } catch {}
  };

  const importSheet = async () => {
    if (!sheetUrl.trim()) return;
    setImportingSheet(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/sheets/import`, {
        method: 'POST', headers,
        body: JSON.stringify({ url: sheetUrl.trim() })
      });
      if (res.ok) {
        setSheetUrl('');
        fetchSheets();
      } else {
        const err = await res.json();
        alert(err.detail || 'Import failed');
      }
    } catch { alert('Import failed'); }
    setImportingSheet(false);
  };

  const deleteSheet = async (sheetId) => {
    if (!window.confirm('Remove this imported sheet?')) return;
    await fetch(`${API_URL}/api/admin/sheets/${sheetId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchSheets();
  };

  const viewSheetDetails = async (sheetId) => {
    if (expandedSheet === sheetId) { setExpandedSheet(null); return; }
    if (!sheetDetails[sheetId]) {
      const res = await fetch(`${API_URL}/api/admin/sheets/${sheetId}/details`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSheetDetails(prev => ({ ...prev, [sheetId]: data }));
      }
    }
    setExpandedSheet(sheetId);
  };

  const fetchSlots = async () => {
    const res = await fetch(`${API_URL}/api/admin/slots/all`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      setSlots(data.slots || []);
    }
  };

  const addSlots = async () => {
    if (!selectedEvent || !newSlotDayLabel || selectedTimes.length === 0) return;
    setAddingSlots(true);
    for (const time of selectedTimes) {
      await fetch(`${API_URL}/api/admin/events/${selectedEvent}/slots`, {
        method: 'POST', headers,
        body: JSON.stringify({ date: newSlotDate || newSlotDayLabel, day_label: newSlotDayLabel, time })
      });
    }
    setNewSlotDate('');
    setNewSlotDayLabel('');
    setSelectedTimes([]);
    setAddingSlots(false);
    fetchSlots();
  };

  const toggleTime = (t) => {
    setSelectedTimes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const deleteSlot = async (slotId) => {
    if (!selectedEvent) return;
    await fetch(`${API_URL}/api/admin/events/${selectedEvent}/slots/${slotId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchSlots();
  };

  const unbookSlot = async (slot) => {
    if (!selectedEvent || !slot.booked_by) return;
    if (!window.confirm(`Remove ${slot.booked_by.name}'s booking for ${slot.day_label}, ${slot.time}? The slot will become available again.`)) return;
    const res = await fetch(`${API_URL}/api/admin/events/${selectedEvent}/slots/${slot.id}/unbook`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      setSlotInfoId(null);
      fetchSlots();
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><p className="text-white">Loading...</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      {/* Reminder Popup */}
      {showReminders && reminders.length > 0 && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowReminders(false)}>
          <div className="bg-[#1A1A1D] border border-white/20 rounded-xl w-full max-w-md p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2"><Bell className="w-5 h-5 text-[#F5A623]" /> Pending Reminders ({reminders.length})</h2>
              <button onClick={() => setShowReminders(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {reminders.map(r => (
                <div key={r.id} className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm">{r.booking_name}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{r.message}</p>
                      {r.due_date && <p className="text-[#F5A623] text-xs mt-0.5">Due: {r.due_date}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => markReminderDone(r.id)} className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30 font-medium">Done</button>
                      <button onClick={() => dismissReminder(r.id)} className="p-1 text-gray-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-white/10 px-3 sm:px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <div className="w-8 h-8 bg-[#F5A623] rounded-lg flex items-center justify-center text-black font-bold text-sm">SW</div>
          Admin Dashboard
        </h1>
        <div className="flex items-center gap-2">
          {sigPendingTotal > 0 && (
            <button onClick={() => setActiveTab('signature')} className="relative text-gray-400 hover:text-[#F5A623] transition-colors p-2" data-testid="reminder-bell-btn" title="Signature Pending">
              <FileSignature className="w-5 h-5" />
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{sigPendingTotal}</span>
            </button>
          )}
          <Button data-testid="admin-logout-btn" onClick={logout} variant="ghost" className="text-gray-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8 space-y-6 sm:space-y-8">
        {/* Webinar Settings */}
        <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
          <h2 data-testid="settings-section-title" className="text-lg font-bold mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#F5A623]" /> Webinar Date & Time
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="settings-date-input" variant="outline" className="w-full h-10 justify-start text-left font-normal bg-[#0A0A0B] border-white/20 text-white hover:bg-[#0A0A0B] hover:text-white">
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#F5A623]" />
                    {date || 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1A1A1D] border-white/20" align="start">
                  <Calendar
                    mode="single"
                    selected={undefined}
                    onSelect={(d) => {
                      if (d) {
                        const day = d.getDate();
                        const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
                        const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
                        const month = d.toLocaleDateString('en-US', { month: 'long' });
                        const year = d.getFullYear();
                        setDate(`${day}${suffix} ${month} ${year} (${dayName})`);
                      }
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Time</Label>
              <Input
                data-testid="settings-time-input"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bg-[#0A0A0B] border-white/20 text-white"
                placeholder="e.g. 8.00PM (Singapore Time)"
              />
            </div>
          </div>
          <div className="space-y-2 mb-4">
            <Label className="text-gray-300 flex items-center gap-1"><Video className="w-4 h-4 text-[#F5A623]" /> Zoom Meeting Link</Label>
            <Input
              data-testid="settings-zoom-input"
              value={zoomLink}
              onChange={(e) => setZoomLink(e.target.value)}
              className="bg-[#0A0A0B] border-white/20 text-white"
              placeholder="e.g. https://zoom.us/j/1234567890"
            />
            <p className="text-gray-500 text-xs">This link will be included in the registration confirmation email.</p>
          </div>
          <div className="space-y-2 mb-4">
            <Label className="text-gray-300 flex items-center gap-1"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WATI Template Name</Label>
            <select
              data-testid="settings-wati-input"
              value={watiTemplate}
              onChange={(e) => setWatiTemplate(e.target.value)}
              className="w-full bg-[#0A0A0B] border border-white/20 text-white rounded-md px-3 h-10 focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50"
            >
              <option value="">-- Select a template --</option>
              {watiTemplates.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} {t.status === 'APPROVED' ? '' : `(${t.status})`}
                </option>
              ))}
            </select>
            <p className="text-gray-500 text-xs">WhatsApp template triggered on each new registration via WATI.</p>
          </div>
          <div className="space-y-2 mb-4">
            <Label className="text-gray-300 flex items-center gap-1"><Code className="w-4 h-4 text-[#F5A623]" /> Meta Pixel ID</Label>
            <Input
              data-testid="settings-tracking-input"
              value={trackingPixel}
              onChange={(e) => setTrackingPixel(e.target.value.replace(/\D/g, '').slice(0, 16))}
              className="bg-[#0A0A0B] border-white/20 text-white font-mono"
              placeholder="e.g. 12345678901234"
              maxLength={16}
            />
            <p className="text-gray-500 text-xs">Enter your 14-16 digit Meta Pixel ID. The full pixel code will be auto-generated.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300 flex items-center gap-1"><Settings className="w-4 h-4 text-[#F5A623]" /> SGD to MYR Rate</Label>
            <Input data-testid="sgd-rate-input" value={sgdRate} onChange={e => setSgdRate(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white w-32" placeholder="3.20" type="number" step="0.01" />
            <p className="text-gray-500 text-xs">Used to convert SGD collections to MYR in stats.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="settings-save-btn" onClick={saveSettings} className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold">
              <Save className="w-4 h-4 mr-2" /> Save Settings
            </Button>
            {settingsSaved && <span className="text-green-400 text-sm">Saved!</span>}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-[#1A1A1D] border border-white/10 rounded-xl p-1 w-fit max-w-full overflow-x-auto">
          <button
            onClick={() => setActiveTab('registrations')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === 'registrations' ? 'bg-[#F5A623] text-black' : 'text-gray-400 hover:text-white'}`}
          >
            <Users className="w-4 h-4" /> Registrations ({totalRegs})
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === 'leads' ? 'bg-[#F5A623] text-black' : 'text-gray-400 hover:text-white'}`}
          >
            <Target className="w-4 h-4" /> Lead Source
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === 'events' ? 'bg-[#F5A623] text-black' : 'text-gray-400 hover:text-white'}`}
          >
            <CalendarPlus className="w-4 h-4" /> Events
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === 'analytics' ? 'bg-[#F5A623] text-black' : 'text-gray-400 hover:text-white'}`}
          >
            <BarChart3 className="w-4 h-4" /> Analytics
          </button>
          <button
            onClick={() => setActiveTab('signature')}
            className={`px-3 sm:px-5 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap ${activeTab === 'signature' ? 'bg-[#F5A623] text-black' : 'text-gray-400 hover:text-white'}`}
            data-testid="tab-signature-pending"
          >
            <FileSignature className="w-4 h-4" /> Signature {sigPendingTotal > 0 && <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{sigPendingTotal}</span>}
          </button>
        </div>

        {activeTab === 'registrations' && (
        <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 data-testid="registrations-section-title" className="text-lg font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-[#F5A623]" /> Registrations ({totalRegs})
            </h2>
          </div>

          {/* Campaign Tabs */}
          {Object.keys(campaigns).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              <button
                onClick={() => setActiveCampaign('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeCampaign === 'all' ? 'bg-[#F5A623] text-black' : 'bg-[#0A0A0B] text-gray-400 border border-white/10 hover:border-white/30'}`}
              >
                All ({totalRegs})
              </button>
              {Object.entries(campaigns).map(([campaign, regs]) => (
                <button
                  key={campaign}
                  onClick={() => { if (!longPressTriggered.current) setActiveCampaign(campaign); }}
                  onMouseDown={() => startLongPress(campaign)}
                  onMouseUp={cancelLongPress}
                  onMouseLeave={cancelLongPress}
                  onTouchStart={() => startLongPress(campaign)}
                  onTouchEnd={cancelLongPress}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors select-none ${activeCampaign === campaign ? 'bg-[#F5A623] text-black' : 'bg-[#0A0A0B] text-gray-400 border border-white/10 hover:border-white/30'}`}
                >
                  {campaign} ({regs.length})
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-4 mb-6 p-4 bg-[#0A0A0B] rounded-lg border border-white/5">
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="download-start-date" variant="outline" className="w-44 h-9 justify-start text-left font-normal bg-[#1A1A1D] border-white/20 text-white hover:bg-[#1A1A1D] hover:text-white">
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#F5A623]" />
                    {startDate ? format(new Date(startDate + 'T00:00:00'), 'dd MMM yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1A1A1D] border-white/20" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate ? new Date(startDate + 'T00:00:00') : undefined}
                    onSelect={(d) => setStartDate(d ? format(d, 'yyyy-MM-dd') : '')}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button data-testid="download-end-date" variant="outline" className="w-44 h-9 justify-start text-left font-normal bg-[#1A1A1D] border-white/20 text-white hover:bg-[#1A1A1D] hover:text-white">
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#F5A623]" />
                    {endDate ? format(new Date(endDate + 'T00:00:00'), 'dd MMM yyyy') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1A1A1D] border-white/20" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate ? new Date(endDate + 'T00:00:00') : undefined}
                    onSelect={(d) => setEndDate(d ? format(d, 'yyyy-MM-dd') : '')}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button data-testid="download-csv-btn" onClick={downloadCSV} variant="outline" className="border-white/20 text-white hover:bg-white/10 h-9">
              <Download className="w-4 h-4 mr-2" /> Download CSV
            </Button>
            {(startDate || endDate) && (
              <button onClick={() => { setStartDate(''); setEndDate(''); }} className="text-gray-400 hover:text-white text-xs underline h-9 flex items-center">
                Clear dates
              </button>
            )}
          </div>

          {(() => {
            const displayRegs = activeCampaign === 'all' ? registrations : (campaigns[activeCampaign] || []);
            return displayRegs.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No registrations yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-400 text-sm">
                      <th className="pb-3 pr-4">#</th>
                      <th className="pb-3 pr-4">Name</th>
                      <th className="pb-3 pr-4">Email</th>
                      <th className="pb-3 pr-4">Phone</th>
                      <th className="pb-3 pr-4">Campaign</th>
                      <th className="pb-3">Registered At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayRegs.map((reg, i) => (
                      <tr key={reg.id || i} className="border-b border-white/5 text-sm">
                        <td className="py-3 pr-4 text-gray-400">{i + 1}</td>
                        <td className="py-3 pr-4">{reg.name}</td>
                        <td className="py-3 pr-4 text-gray-300">{reg.email}</td>
                        <td className="py-3 pr-4 text-gray-300">{reg.phone}</td>
                        <td className="py-3 pr-4 text-[#F5A623] text-xs">{reg.campaign || 'Uncategorized'}</td>
                        <td className="py-3 text-gray-400">{new Date(reg.registered_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
        )}

        {activeTab === 'leads' && (
        <div className="space-y-6">
          {/* UTM Link Builder */}
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
              <Link className="w-5 h-5 text-[#F5A623]" /> UTM Link Builder
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Source</Label>
                <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. facebook" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Medium</Label>
                <Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. broad" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Campaign</Label>
                <Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. sowjantest" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Term</Label>
                <Input value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. test" />
              </div>
            </div>
            {(utmSource || utmMedium || utmCampaign || utmTerm) && (() => {
              const params = new URLSearchParams();
              if (utmSource) params.append('utm_source', utmSource);
              if (utmMedium) params.append('utm_medium', utmMedium);
              if (utmCampaign) params.append('utm_campaign', utmCampaign);
              if (utmTerm) params.append('utm_term', utmTerm);
              const generatedUrl = `${window.location.origin}/optin?${params.toString()}`;
              return (
                <div className="flex items-center gap-2 p-3 bg-[#0A0A0B] rounded-lg border border-white/5">
                  <code className="text-[#F5A623] text-sm flex-1 break-all">{generatedUrl}</code>
                  <Button
                    variant="ghost"
                    className="text-gray-400 hover:text-white shrink-0"
                    onClick={() => { navigator.clipboard.writeText(generatedUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  >
                    <Copy className="w-4 h-4 mr-1" /> {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              );
            })()}
          </div>

          {/* Lead Table */}
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Target className="w-5 h-5 text-[#F5A623]" /> Lead Source Data
              </h2>
              <Button onClick={downloadCSV} variant="outline" className="border-white/20 text-white hover:bg-white/10 h-9">
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </Button>
            </div>

            {(() => {
              const leadsWithUtm = registrations.filter(r => r.utm_source || r.utm_medium || r.utm_campaign || r.utm_term);
              return leadsWithUtm.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No lead source data yet. Registrations from UTM links will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-white/10 text-gray-400 text-sm">
                        <th className="pb-3 pr-4">#</th>
                        <th className="pb-3 pr-4">Name</th>
                        <th className="pb-3 pr-4">Email</th>
                        <th className="pb-3 pr-4">Source</th>
                        <th className="pb-3 pr-4">Medium</th>
                        <th className="pb-3 pr-4">Campaign</th>
                        <th className="pb-3 pr-4">Term</th>
                        <th className="pb-3">Registered At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leadsWithUtm.map((reg, i) => (
                        <tr key={reg.id || i} className="border-b border-white/5 text-sm">
                          <td className="py-3 pr-4 text-gray-400">{i + 1}</td>
                          <td className="py-3 pr-4">{reg.name}</td>
                          <td className="py-3 pr-4 text-gray-300">{reg.email}</td>
                          <td className="py-3 pr-4"><span className="bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded text-xs">{reg.utm_source || '-'}</span></td>
                          <td className="py-3 pr-4"><span className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs">{reg.utm_medium || '-'}</span></td>
                          <td className="py-3 pr-4"><span className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs">{reg.utm_campaign || '-'}</span></td>
                          <td className="py-3 pr-4"><span className="bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded text-xs">{reg.utm_term || '-'}</span></td>
                          <td className="py-3 text-gray-400">{new Date(reg.registered_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </div>
        )}

        {activeTab === 'events' && (
        <div className="space-y-6">
          {/* Create Event */}
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
              <CalendarPlus className="w-5 h-5 text-[#F5A623]" /> Create New Event
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Event Name *</Label>
                <Input value={newEvent.name} onChange={(e) => setNewEvent({...newEvent, name: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. 1-on-1 Consultation with Steven" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">URL Slug *</Label>
                <Input value={newEvent.slug} onChange={(e) => setNewEvent({...newEvent, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. consultation-may" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Date</Label>
                <Input value={newEvent.date} onChange={(e) => setNewEvent({...newEvent, date: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. 28th May 2026" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Time</Label>
                <Input value={newEvent.time} onChange={(e) => setNewEvent({...newEvent, time: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="e.g. 3.00PM" />
              </div>
            </div>
            <div className="space-y-1 mb-4">
              <Label className="text-gray-400 text-xs">Description (optional)</Label>
              <Input value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="bg-[#0A0A0B] border-white/20 text-white" placeholder="Brief description of the event" />
            </div>
            <Button onClick={createEvent} className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold">
              <CalendarPlus className="w-4 h-4 mr-2" /> Create Event
            </Button>
          </div>

          {/* Events List */}
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
              <CalendarPlus className="w-5 h-5 text-[#F5A623]" /> Your Events ({events.length})
            </h2>
            {events.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No events yet. Create one above.</p>
            ) : (
              <div className="space-y-3">
                {events.map((ev) => {
                  const eventUrl = `${window.location.origin}/event/${ev.slug}`;
                  const isEditing = editingEvent && editingEvent.slug === ev.slug;
                  return (
                    <div key={ev.slug} className="bg-[#0A0A0B] border border-white/5 rounded-lg p-4">
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="grid sm:grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-gray-400 text-xs">Event Name</Label>
                              <Input value={editingEvent.name} onChange={(e) => setEditingEvent({...editingEvent, name: e.target.value})} className="bg-[#1A1A1D] border-white/20 text-white h-9" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-gray-400 text-xs">Date</Label>
                              <Input value={editingEvent.date} onChange={(e) => setEditingEvent({...editingEvent, date: e.target.value})} className="bg-[#1A1A1D] border-white/20 text-white h-9" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-gray-400 text-xs">Time</Label>
                              <Input value={editingEvent.time} onChange={(e) => setEditingEvent({...editingEvent, time: e.target.value})} className="bg-[#1A1A1D] border-white/20 text-white h-9" />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-gray-400 text-xs">Description</Label>
                            <Input value={editingEvent.description || ''} onChange={(e) => setEditingEvent({...editingEvent, description: e.target.value})} className="bg-[#1A1A1D] border-white/20 text-white h-9" />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={saveEvent} className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-8 text-xs px-4">
                              <Save className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button onClick={() => setEditingEvent(null)} variant="ghost" className="text-gray-400 hover:text-white h-8 text-xs">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-white font-bold">{ev.name}</h3>
                              <div className="flex flex-wrap gap-3 text-sm text-gray-400 mt-1">
                                {ev.date && <span>{ev.date}</span>}
                                {ev.time && <span>{ev.time}</span>}
                                <span className="text-[#F5A623]">{ev.booking_count || 0} bookings</span>
                              </div>
                            </div>
                            <div className="flex gap-1 sm:gap-2 shrink-0 flex-wrap">
                              <Button variant="ghost" size="sm" onClick={() => setEditingEvent({...ev})} className="text-gray-400 hover:text-white" title="Edit">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => cloneEvent(ev.slug)} className="text-gray-400 hover:text-white" title="Clone" data-testid={`clone-event-${ev.slug}`}>
                                <CopyPlus className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => viewBookings(ev.slug)} className="text-gray-400 hover:text-white">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => downloadEventCSV(ev.slug)} className="text-gray-400 hover:text-white">
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteEvent(ev.slug)} className="text-gray-400 hover:text-red-400">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2 p-2 bg-[#1A1A1D] rounded border border-white/5 overflow-hidden">
                            <code className="text-[#F5A623] text-xs flex-1 truncate">{eventUrl}</code>
                            <button
                              onClick={() => { navigator.clipboard.writeText(eventUrl); setEventCopied(ev.slug); setTimeout(() => setEventCopied(''), 2000); }}
                              className="text-gray-400 hover:text-white text-xs flex items-center gap-1 shrink-0"
                            >
                              <Copy className="w-3 h-3" /> {eventCopied === ev.slug ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                        </>
                      )}
                      {/* Expanded content when this event is selected */}
                      {selectedEvent === ev.slug && (
                        <div className="mt-4 space-y-4 border-t border-white/10 pt-4">
                          {/* Availability Manager */}
                          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
                            <button
                              onClick={() => setSlotsCollapsed(!slotsCollapsed)}
                              className="w-full flex items-center justify-between"
                            >
                              <h2 className="text-lg font-bold flex items-center gap-2">
                                <CalendarIcon className="w-5 h-5 text-[#F5A623]" /> Availability Slots
                              </h2>
                              {slotsCollapsed ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronUp className="w-5 h-5 text-gray-400" />}
                            </button>
                            {!slotsCollapsed && (
                            <div className="mt-4">

                {/* Existing Slots list — shown first */}
                {slots.length === 0 ? (
                  <p className="text-gray-500 text-sm mb-4">No slots added yet.</p>
                ) : (() => {
                  const grouped = {};
                  slots.forEach(s => {
                    const key = s.day_label;
                    if (!grouped[key]) grouped[key] = [];
                    grouped[key].push(s);
                  });
                  return (
                    <div className="space-y-3 mb-6">
                      {Object.entries(grouped).sort((a, b) => {
                        // Sort by actual date value
                        const slotsA = a[1], slotsB = b[1];
                        const dateA = slotsA[0]?.date || '';
                        const dateB = slotsB[0]?.date || '';
                        return dateA.localeCompare(dateB);
                      }).map(([day, daySlots]) => (
                        <div key={day}>
                          <p className="text-sm font-medium text-gray-300 mb-1.5">{day}</p>
                          <div className="flex flex-wrap gap-2">
                            {daySlots.sort((a, b) => {
                              const parse = (t) => {
                                const m = t.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
                                if (!m) return 0;
                                let h = parseInt(m[1]), min = parseInt(m[2]);
                                if (m[3].toUpperCase() === 'PM' && h !== 12) h += 12;
                                if (m[3].toUpperCase() === 'AM' && h === 12) h = 0;
                                return h * 60 + min;
                              };
                              return parse(a.time) - parse(b.time);
                            }).map(slot => {
                              // Look up interview status for booked person
                              let slotStatus = '';
                              if (slot.booked_by) {
                                const match = eventBookings.find(b => b.phone === slot.booked_by.phone || b.email === slot.booked_by.email);
                                if (match?.interview?.status) slotStatus = match.interview.status;
                              }
                              const statusColor = slotStatus === 'Successful Payment' ? 'text-green-400' : slotStatus === 'Pending' ? 'text-blue-400' : slotStatus === 'Follow Up' ? 'text-yellow-400' : slotStatus === 'Not Ready' ? 'text-red-400' : '';
                              const badgeBg = slotStatus === 'Successful Payment' ? 'bg-green-500/10 border-green-500/20' : slot.booked_by ? 'bg-red-500/10 border-red-500/20' : 'bg-[#0A0A0B] border-white/10';
                              return (
                              <div key={slot.id}>
                                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${badgeBg}`}>
                                  <Clock className="w-3.5 h-3.5 text-[#F5A623]" />
                                  <span className={slot.booked_by ? 'text-red-300' : 'text-white'}>{slot.time}</span>
                                  {slot.booked_by ? (
                                    <>
                                      <span
                                        className="text-red-400 text-xs ml-1 cursor-pointer hover:text-red-300 hover:underline"
                                        title={`Click to view ${slot.booked_by.name}'s info`}
                                        onClick={() => setSlotInfoId(slotInfoId === slot.id ? null : slot.id)}
                                      >
                                        — {slot.booked_by.name}
                                      </span>
                                      {slotStatus && <span className={`text-xs font-medium ${statusColor} ml-1`}>• {slotStatus}</span>}
                                      <button
                                        data-testid={`slot-unbook-${slot.id}`}
                                        onClick={() => unbookSlot(slot)}
                                        className="text-gray-600 hover:text-red-400 ml-1 transition-colors"
                                        title={`Cancel ${slot.booked_by.name}'s booking`}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </>
                                  ) : (
                                    <button data-testid={`slot-delete-${slot.id}`} onClick={() => deleteSlot(slot.id)} className="text-gray-500 hover:text-red-400 ml-1">
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                {slot.booked_by && slotInfoId === slot.id && (() => {
                                  return (
                                    <div className="mt-2 bg-[#1A1A1D] border border-white/10 rounded-lg p-3 text-xs">
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-white font-medium text-sm">{slot.booked_by.name}</span>
                                        <button onClick={() => setSlotInfoId(null)} className="text-gray-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
                                      </div>
                                      <div className="space-y-1 text-gray-400">
                                        <p>Phone: <span className="text-gray-200">{slot.booked_by.phone}</span></p>
                                        <p>Email: <span className="text-gray-200">{slot.booked_by.email}</span></p>
                                        {slot.booked_by.booked_at && <p>Booked at: <span className="text-gray-200">{new Date(slot.booked_by.booked_at).toLocaleString()}</span></p>}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )})}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Add New Slots */}
                <div className="mb-4 pt-4 border-t border-white/10">
                  <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-3">Add New Slots</p>
                  <div className="space-y-1 mb-3">
                    <Label className="text-gray-400 text-xs">Pick a date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button data-testid="slot-day-input" variant="outline" className="w-72 h-10 justify-start text-left font-normal bg-[#0A0A0B] border-white/20 text-white hover:bg-[#0A0A0B] hover:text-white truncate">
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#F5A623]" />
                          {newSlotDayLabel || 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1A1A1D] border-white/20" align="start">
                        <Calendar
                          mode="single"
                          selected={undefined}
                          onSelect={(d) => {
                            if (d) {
                              const day = d.getDate();
                              const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
                              const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
                              const month = d.toLocaleDateString('en-US', { month: 'long' });
                              const year = d.getFullYear();
                              setNewSlotDayLabel(`${dayName}, ${day}${suffix} ${month} ${year}`);
                              setNewSlotDate(format(d, 'yyyy-MM-dd'));
                              setSelectedTimes([]);
                            }
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {newSlotDayLabel && (
                    <div className="p-4 bg-[#0A0A0B] rounded-lg border border-white/10">
                      <p className="text-gray-400 text-xs mb-2">Select time slots for <span className="text-white font-medium">{newSlotDayLabel}</span></p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {['10:00AM','11:00AM','12:00PM','1:00PM','2:00PM','3:00PM','4:00PM','5:00PM','6:00PM','7:00PM','8:00PM','9:00PM'].map(t => {
                          const alreadyExists = slots.some(s => s.day_label === newSlotDayLabel && s.time === t);
                          const isSelected = selectedTimes.includes(t);
                          return (
                            <button
                              key={t}
                              data-testid={`slot-time-${t}`}
                              disabled={alreadyExists}
                              onClick={() => toggleTime(t)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                alreadyExists
                                  ? 'bg-white/5 text-gray-600 cursor-not-allowed line-through'
                                  : isSelected
                                    ? 'bg-[#F5A623] text-black ring-2 ring-[#F5A623]/50'
                                    : 'bg-[#1A1A1D] text-gray-300 border border-white/10 hover:border-[#F5A623]/50 hover:text-white cursor-pointer'
                              }`}
                            >
                              <Clock className="w-3 h-3 inline mr-1" />{t}
                            </button>
                          );
                        })}
                      </div>
                      {selectedTimes.length > 0 && (
                        <div className="flex items-center gap-3">
                          <Button
                            data-testid="slot-confirm-btn"
                            onClick={addSlots}
                            disabled={addingSlots}
                            className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-9"
                          >
                            {addingSlots ? 'Adding...' : `Confirm ${selectedTimes.length} slot${selectedTimes.length > 1 ? 's' : ''}`}
                          </Button>
                          <span className="text-gray-500 text-xs">{selectedTimes.sort().join(', ')}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Links */}
                <div className="flex items-center gap-2 mb-2 p-2.5 bg-[#0A0A0B] rounded-lg border border-white/5">
                  <span className="text-gray-400 text-xs shrink-0">Booking page:</span>
                  <code className="text-[#F5A623] text-xs flex-1 break-all">{`${window.location.origin}/book/${selectedEvent}`}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/${selectedEvent}`); }}
                    className="text-gray-400 hover:text-white text-xs flex items-center gap-1 shrink-0"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-4 p-2.5 bg-[#0A0A0B] rounded-lg border border-white/5">
                  <span className="text-gray-400 text-xs shrink-0">Application form:</span>
                  <span className="text-gray-500 text-xs">{window.location.origin}/apply/</span>
                  <input
                    value={formSlug}
                    onChange={(e) => setFormSlug(e.target.value)}
                    className="bg-transparent border-b border-dashed border-[#F5A623]/40 text-[#F5A623] text-xs outline-none flex-1 min-w-[100px]"
                  />
                  <button
                    onClick={saveFormSlug}
                    className="text-[#F5A623] hover:text-white text-xs flex items-center gap-1 shrink-0 font-medium"
                  >
                    <Save className="w-3 h-3" /> {formSlugSaved ? 'Saved!' : 'Save'}
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/apply/${formSlug}`); }}
                    className="text-gray-400 hover:text-white text-xs flex items-center gap-1 shrink-0"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-4 p-2.5 bg-[#0A0A0B] rounded-lg border border-white/5">
                  <span className="text-gray-400 text-xs shrink-0">Signature upload:</span>
                  <span className="text-gray-500 text-xs">{window.location.origin}/signature/</span>
                  <input
                    value={sigSlug}
                    onChange={(e) => setSigSlug(e.target.value)}
                    className="bg-transparent border-b border-dashed border-purple-400/40 text-purple-400 text-xs outline-none flex-1 min-w-[80px]"
                  />
                  <button
                    onClick={saveSigSlug}
                    className="text-purple-400 hover:text-white text-xs flex items-center gap-1 shrink-0 font-medium"
                  >
                    <Save className="w-3 h-3" /> {sigSlugSaved ? 'Saved!' : 'Save'}
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/signature/${sigSlug}`); }}
                    className="text-gray-400 hover:text-white text-xs flex items-center gap-1 shrink-0"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
                <p className="text-gray-500 text-xs mb-4">Placeholders: <code className="text-[#F5A623]">{'{name}'}</code> <code className="text-[#F5A623]">{'{booked_date}'}</code> <code className="text-[#F5A623]">{'{booked_time}'}</code> <code className="text-[#F5A623]">{'{booking_link}'}</code> <code className="text-[#F5A623]">{'{application_form_link}'}</code> <code className="text-[#F5A623]">{'{signature_link}'}</code> — auto-replaced when sent.</p>
                </div>
                )}
              </div>

              {/* Bookings + WhatsApp */}
              <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                  <h2 className="text-base sm:text-lg font-bold flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#F5A623]" /> <span className="truncate">Bookings: {selectedEvent}</span> ({eventBookings.length})
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      <Clock className="w-3 h-3 inline mr-1" />{Math.floor(refreshCountdown / 60)}:{String(refreshCountdown % 60).padStart(2, '0')}
                    </span>
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectMode && selectedBookings.size > 0 && (
                      <Button onClick={deleteSelectedBookings} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9" data-testid="delete-selected-btn">
                        <Trash2 className="w-4 h-4 mr-1" /> Delete ({selectedBookings.size})
                      </Button>
                    )}
                    <Button onClick={() => { setSelectMode(!selectMode); setSelectedBookings(new Set()); }} variant="outline" className={`h-9 ${selectMode ? 'border-[#F5A623]/50 text-[#F5A623] hover:bg-[#F5A623]/10' : 'border-white/20 text-white hover:bg-white/10'}`} data-testid="select-mode-btn">
                      {selectMode ? 'Cancel' : 'Select'}
                    </Button>
                    <label className={`inline-flex items-center gap-2 px-3 h-9 rounded-md text-sm font-medium cursor-pointer border transition-colors ${uploadingCsv ? 'border-gray-600 text-gray-400' : 'border-white/20 text-white hover:bg-white/10'}`}>
                      <Download className="w-4 h-4 rotate-180" />
                      {uploadingCsv ? 'Importing...' : 'Upload CSV'}
                      <input type="file" accept=".csv,.txt,.tsv" onChange={(e) => handleCsvUpload(e, false)} disabled={uploadingCsv} className="hidden" />
                    </label>
                    <Button onClick={() => downloadEventCSV(selectedEvent)} variant="outline" className="border-white/20 text-white hover:bg-white/10 h-9">
                      <Download className="w-4 h-4 mr-2" /> Download CSV
                    </Button>
                  </div>
                </div>

                {/* WhatsApp Message Templates */}
                <div className="mb-6 p-4 bg-[#0A0A0B] rounded-lg border border-white/5">
                  <h3 className="text-sm font-bold text-[#25D366] flex items-center gap-2 mb-3">
                    <MessageCircle className="w-4 h-4" /> WhatsApp Messages
                    {waSaved && <span className="text-green-400 text-xs font-normal ml-2">Saved!</span>}
                  </h3>
                  <div className="space-y-2">
                    {[
                      { key: 'message1', label: 'Message 1' },
                      { key: 'message2', label: 'Message 2' },
                      { key: 'message3', label: 'Message 3' },
                      { key: 'message4', label: 'Message 4' }
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-start gap-2">
                        {editingWaMsg === key ? (
                          <div className="flex-1 space-y-2">
                            <textarea
                              data-testid={`wa-edit-${key}`}
                              value={waMsgDraft}
                              onChange={(e) => setWaMsgDraft(e.target.value)}
                              rows={5}
                              className="w-full bg-[#1A1A1D] border border-white/20 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#25D366]/50 resize-y"
                              placeholder={`Enter ${label} text...${key === 'message2' ? ' Use {booking_link} to include the booking page URL.' : ''}`}
                            />
                            <div className="flex gap-2">
                              <Button data-testid={`wa-save-${key}`} onClick={() => saveWaMessage(key, waMsgDraft)} className="bg-[#25D366] hover:bg-[#1ea952] text-white h-7 text-xs px-3">
                                <Save className="w-3 h-3 mr-1" /> Save
                              </Button>
                              <Button onClick={() => { setEditingWaMsg(null); setWaMsgDraft(''); }} variant="ghost" className="text-gray-400 hover:text-white h-7 text-xs">
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-gray-300 shrink-0">{label}</span>
                                {waMessages[key] ? (
                                  <p className="text-xs text-gray-500 truncate">{waMessages[key].substring(0, 80)}...</p>
                                ) : (
                                  <p className="text-xs text-gray-600 italic">Not set</p>
                                )}
                              </div>
                            </div>
                            <button
                              data-testid={`wa-edit-btn-${key}`}
                              onClick={() => { setEditingWaMsg(key); setWaMsgDraft(waMessages[key] || ''); }}
                              className="text-gray-400 hover:text-white text-xs flex items-center gap-1 shrink-0"
                            >
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {eventBookings.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No bookings yet for this event.</p>
                ) : (
                  <>
                  {/* Stats Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                    {(() => {
                      const total = eventBookings.length;
                      const booked = eventBookings.filter(b => {
                        const slot = findBookedSlot(b.name, b.email, b.phone);
                        return !!slot;
                      }).length;
                      const consulted = eventBookings.filter(b => b.interview?.interviewed === 'Yes').length;
                      const successful = eventBookings.filter(b => b.interview?.status === 'Successful Payment').length;
                      const pending = eventBookings.filter(b => b.interview?.status === 'Pending').length;
                      const followUp = eventBookings.filter(b => b.interview?.status === 'Follow Up').length;
                      const notReady = eventBookings.filter(b => b.interview?.status === 'Not Ready').length;
                      const bookedPct = total > 0 ? Math.round(booked / total * 100) : 0;
                      const closingRate = consulted > 0 ? Math.round(successful / consulted * 100) : 0;
                      return (
                        <>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Total Bookings</p>
                            <p data-testid="stat-total" className="text-2xl font-bold text-white">{total}</p>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">WA Messages Sent</p>
                            <div data-testid="stat-wa" className="flex justify-center gap-3 mt-1">
                              {[1,2,3,4].map(n => {
                                const count = eventBookings.filter(b => b[`wa${n}_sent`]).length;
                                return (
                                  <div key={n} className="text-center">
                                    <p className={`text-lg font-bold ${count > 0 ? 'text-[#25D366]' : 'text-gray-600'}`}>{count}</p>
                                    <p className="text-[10px] text-gray-500">Msg {n}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Slot Booked</p>
                            <p data-testid="stat-booked" className="text-2xl font-bold text-[#F5A623]">{booked}<span className="text-sm text-gray-400 font-normal">/{total}</span></p>
                            <p className="text-xs text-gray-500">{bookedPct}%</p>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Total Consultations</p>
                            <p data-testid="stat-consulted" className="text-2xl font-bold text-purple-400">{consulted}<span className="text-sm text-gray-400 font-normal">/{booked}</span></p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3 mb-4">
                    {(() => {
                      const consulted = eventBookings.filter(b => b.interview?.interviewed === 'Yes').length;
                      const successful = eventBookings.filter(b => b.interview?.status === 'Successful Payment').length;
                      const pending = eventBookings.filter(b => b.interview?.status === 'Pending').length;
                      const followUp = eventBookings.filter(b => b.interview?.status === 'Follow Up').length;
                      const notReady = eventBookings.filter(b => b.interview?.status === 'Not Ready').length;
                      const closingRate = consulted > 0 ? Math.round(successful / consulted * 100) : 0;
                      let rmTotal = 0, sgdTotal = 0;
                      let rmExpected = 0, sgdExpected = 0;
                      let rmDeposit = 0, sgdDeposit = 0;
                      let totalFees = 0;
                      const ALL_GATEWAYS = [
                        { label: 'MY Direct Bank Transfer', fee: 0 }, { label: 'SG Direct Bank Transfer', fee: 0 }, { label: 'SG Paynow', fee: 0 }, { label: 'Billplz FPX', fee: 0 },
                        { label: 'Billplz Senangpay', fee: 1.8 }, { label: '2C2P', fee: 2.5 }, { label: 'Stripe', fee: 3.5 }, { label: 'Stripe SG', fee: 3.5 },
                        { label: 'Senangpay (Card)', fee: 2.5 }, { label: 'Senangpay (FPX)', fee: 1.5 },
                        { label: 'CHIP FPX', fee: 0 }, { label: 'CHIP Credit Card (Local)', fee: 1.8 }, { label: 'CHIP Debit Card (Local)', fee: 1.0 },
                        { label: "CHIP Credit Card (Int'l)", fee: 2.5 }, { label: "CHIP Debit Card (Int'l)", fee: 1.5 },
                        { label: 'Payex RHB 6 Months', fee: 4.0 }, { label: 'Payex RHB 12 Months', fee: 5.0 }, { label: 'Payex RHB 24 Months', fee: 7.5 }, { label: 'Payex RHB 36 Months', fee: 8.0 },
                        { label: 'Payex Ambank 6 Months', fee: 4.0 }, { label: 'Payex Ambank 12 Months', fee: 4.5 }, { label: 'Payex Ambank 24 Months', fee: 7.0 }, { label: 'Payex Ambank 36 Months', fee: 8.0 }, { label: 'Payex Ambank 48 Months', fee: 8.5 }, { label: 'Payex Ambank 60 Months', fee: 9.0 },
                        { label: 'Payex PBB 6 Months', fee: 5.0 }, { label: 'Payex PBB 12 Months', fee: 6.0 }, { label: 'Payex PBB 24 Months', fee: 7.0 }, { label: 'Payex PBB 36 Months', fee: 7.5 },
                        { label: 'Payex OCBC 6 Months', fee: 4.0 }, { label: 'Payex OCBC 12 Months', fee: 5.0 }, { label: 'Payex OCBC 24 Months', fee: 7.0 }, { label: 'Payex OCBC 36 Months', fee: 8.0 },
                        { label: 'Payex CIMB 6 Months', fee: 6.0 }, { label: 'Payex CIMB 12 Months', fee: 6.5 }, { label: 'Payex CIMB 24 Months', fee: 8.0 }, { label: 'Payex CIMB 36 Months', fee: 8.5 }, { label: 'Payex CIMB 48 Months', fee: 9.5 }, { label: 'Payex CIMB 60 Months', fee: 12.0 },
                        { label: 'Payex UOB 6 Months', fee: 4.0 }, { label: 'Payex UOB 12 Months', fee: 5.0 }, { label: 'Payex UOB 24 Months', fee: 6.5 }, { label: 'Payex UOB 36 Months', fee: 7.0 },
                        { label: 'Payex Maybank 6 Months', fee: 4.5 }, { label: 'Payex Maybank 12 Months', fee: 5.2 }, { label: 'Payex Maybank 18 Months', fee: 6.5 }, { label: 'Payex Maybank 24 Months', fee: 8.0 }, { label: 'Payex Maybank 36 Months', fee: 8.5 },
                        { label: 'Payex Affin 6 Months', fee: 3.8 }, { label: 'Payex Affin 12 Months', fee: 5.0 }, { label: 'Payex Affin 24 Months', fee: 6.0 }, { label: 'Payex Affin 36 Months', fee: 7.5 },
                        { label: 'Payex HLB 6 Months', fee: 6.0 }, { label: 'Payex HLB 12 Months', fee: 6.5 }, { label: 'Payex HLB 24 Months', fee: 7.0 }, { label: 'Payex HLB 36 Months', fee: 7.5 }, { label: 'Payex HLB 48 Months', fee: 9.0 }, { label: 'Payex HLB 60 Months', fee: 10.0 },
                        { label: 'Payex HSBC 6 Months', fee: 5.0 }, { label: 'Payex HSBC 12 Months', fee: 6.0 }, { label: 'Payex HSBC 24 Months', fee: 7.0 }, { label: 'Payex HSBC 36 Months', fee: 8.0 },
                        { label: 'Payex Std Chart 6 Months', fee: 5.0 }, { label: 'Payex Std Chart 12 Months', fee: 6.0 }, { label: 'Payex Std Chart 24 Months', fee: 7.0 }, { label: 'Payex Std Chart 36 Months', fee: 8.0 },
                      ];
                      const GW_MAP = Object.fromEntries(ALL_GATEWAYS.map(g => [g.label, g.fee]));
                      eventBookings.forEach(b => {
                        const iv = b.interview || {};
                        const amt = iv.payment_amount || '';
                        const program = iv.program || '';
                        let paymentVal = 0;
                        // Parse actual collected amount
                        if (amt) {
                          const nums = amt.replace(/,/g, '').match(/[\d.]+/);
                          if (nums) {
                            paymentVal = parseFloat(nums[0]);
                            if (amt.toLowerCase().includes('sgd')) {
                              sgdTotal += paymentVal;
                              if (iv.payment_method === 'Deposit') sgdDeposit += paymentVal;
                            } else {
                              rmTotal += paymentVal;
                              if (iv.payment_method === 'Deposit') rmDeposit += paymentVal;
                            }
                          }
                        }
                        // Calculate fee from gateway percentage × payment amount
                        if (iv.gateway && paymentVal > 0) {
                          const feePercent = GW_MAP[iv.gateway] ?? 0;
                          totalFees += paymentVal * (feePercent / 100);
                        }
                        // Parse expected amount from program selection
                        if (program && iv.status === 'Successful Payment') {
                          const progMatch = program.match(/(RM|SGD)([\d,]+)/i);
                          if (progMatch) {
                            const progVal = parseFloat(progMatch[2].replace(/,/g, ''));
                            if (progMatch[1].toUpperCase() === 'SGD') sgdExpected += progVal;
                            else rmExpected += progVal;
                          }
                        }
                      });
                      const rmBalance = rmExpected - rmTotal;
                      const sgdBalance = sgdExpected - sgdTotal;
                      const SGD_TO_RM = sgdRate;
                      const actualReceived = (rmTotal + (sgdTotal * SGD_TO_RM)) - totalFees;
                      return (
                        <>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Successful</p>
                            <p className="text-2xl font-bold text-green-400">{successful}</p>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Pending</p>
                            <p className="text-2xl font-bold text-blue-400">{pending}</p>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Follow Up</p>
                            <p className="text-2xl font-bold text-yellow-400">{followUp}</p>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Not Ready</p>
                            <p className="text-2xl font-bold text-red-400">{notReady}</p>
                          </div>
                          <div className="bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-center">
                            <p className="text-gray-400 text-xs mb-1">Closing Rate</p>
                            <p className={`text-2xl font-bold ${closingRate >= 50 ? 'text-green-400' : closingRate >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>{closingRate}%</p>
                          </div>
                          <div className="col-span-2 sm:col-span-3 md:col-span-5 lg:col-span-4 bg-[#0A0A0B] border border-white/10 rounded-lg p-3 overflow-hidden">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center mb-3">
                              <div>
                                <p className="text-gray-400 text-xs mb-1">Total Collection</p>
                                <p className="text-sm font-bold text-[#F5A623]">RM{rmTotal.toLocaleString()} | SGD{sgdTotal.toLocaleString()}</p>
                                {(rmDeposit > 0 || sgdDeposit > 0) && <p className="text-xs text-gray-500 mt-0.5">(Deposit: {rmDeposit > 0 ? `RM${rmDeposit.toLocaleString()}` : ''}{rmDeposit > 0 && sgdDeposit > 0 ? ' + ' : ''}{sgdDeposit > 0 ? `SGD${sgdDeposit.toLocaleString()}` : ''})</p>}
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs mb-1">Expected</p>
                                <p className="text-sm font-bold text-green-400">RM{rmExpected.toLocaleString()} | SGD{sgdExpected.toLocaleString()}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs mb-1">Balance</p>
                                <p className={`text-sm font-bold ${rmBalance > 0 || sgdBalance > 0 ? 'text-red-400' : 'text-green-400'}`}>RM{rmBalance.toLocaleString()} | SGD{sgdBalance.toLocaleString()}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-center border-t border-white/10 pt-2">
                              <div>
                                <p className="text-gray-400 text-xs mb-1">Total Fees</p>
                                <p className="text-sm font-bold text-orange-400">−{totalFees.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                              </div>
                              <div>
                                <p className="text-gray-400 text-xs mb-1">Actual Received (RM)</p>
                                <p className="text-sm font-bold text-emerald-400">RM{actualReceived.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                                {sgdTotal > 0 && <p className="text-xs text-gray-500 mt-0.5">SGD×{SGD_TO_RM} rate</p>}
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="overflow-x-auto -mx-2 px-2">
                    <table className="w-full text-left table-fixed min-w-[800px]">
                      <thead>
                        <tr className="border-b border-white/10 text-gray-400 text-sm">
                          {selectMode && (
                            <th className="pb-3 pr-2 w-8">
                              <input type="checkbox" checked={selectedBookings.size === eventBookings.length && eventBookings.length > 0} onChange={toggleSelectAll} className="accent-[#F5A623] cursor-pointer" data-testid="select-all-checkbox" />
                            </th>
                          )}
                          <th className="pb-3 pr-2 w-8">#</th>
                          <th className="pb-3 pr-2 w-[22%]">Name</th>
                          <th className="pb-3 pr-2 w-14">Gender</th>
                          <th className="pb-3 pr-2 w-[15%]">Email</th>
                          <th className="pb-3 pr-2 w-[11%]">Phone</th>
                          <th className="pb-3 pr-2 w-20">Booked At</th>
                          <th className="pb-3 text-center w-[22%]">Send WhatsApp</th>
                          <th className="pb-3 text-center w-20">Reminder</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedBookings().map((b, i) => {
                          const bookedSlot = b._slot;
                          const app = findApplication(b.name, b.email, b.phone);
                          const sig = findSignature(b.name, b.email, b.phone);
                          const isExpanded = expandedBooking === (b.id || i);
                          const isInterviewed = b.interview?.interviewed === 'Yes';
                          return (
                          <React.Fragment key={b.id || i}>
                          <tr id={`booking-row-${b.id || i}`} className={`group border-b ${isExpanded ? 'border-[#F5A623]/20' : 'border-white/5'} text-sm ${selectedBookings.has(b.id) ? 'bg-red-500/10' : isInterviewed ? 'bg-purple-500/8' : bookedSlot ? 'bg-[#F5A623]/5' : ''}`}>
                            {selectMode && (
                              <td className="py-3 pr-2">
                                <input type="checkbox" checked={selectedBookings.has(b.id)} onChange={() => toggleBookingSelection(b.id)} className="accent-[#F5A623] cursor-pointer" data-testid={`select-booking-${i}`} />
                              </td>
                            )}
                            <td className="py-3 pr-4 text-gray-400">
                              <div className="flex items-center gap-1.5">
                                {i + 1}
                                <button
                                  data-testid={`delete-booking-${i}`}
                                  onClick={() => deleteBooking(b)}
                                  className="text-gray-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                  title={`Remove ${b.name}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                                <button
                                  data-testid={`reminder-booking-${i}`}
                                  onClick={() => addReminder(b.id, b.name)}
                                  className="text-gray-600 hover:text-[#F5A623] transition-colors opacity-0 group-hover:opacity-100"
                                  title={`Set reminder for ${b.name}`}
                                >
                                  <Bell className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            <td className="py-3 pr-4">
                              {editingName === (b.id || i) ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    data-testid={`name-edit-input-${i}`}
                                    value={nameDraft}
                                    onChange={(e) => setNameDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveName(b.id, nameDraft); if (e.key === 'Escape') setEditingName(null); }}
                                    className="bg-[#1A1A1D] border border-white/20 text-white rounded px-2 py-1 text-sm w-40 focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50"
                                    autoFocus
                                  />
                                  <button onClick={() => saveName(b.id, nameDraft)} className="text-green-400 hover:text-green-300 text-xs">Save</button>
                                  <button onClick={() => setEditingName(null)} className="text-gray-500 hover:text-gray-300 text-xs">Esc</button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setExpandedBooking(isExpanded ? null : (b.id || i))}
                                  onDoubleClick={(e) => { e.stopPropagation(); setEditingName(b.id || i); setNameDraft(b.name || ''); }}
                                  className={`text-left cursor-pointer hover:underline ${isExpanded ? 'text-[#F5A623] font-medium' : 'text-white'}`}
                                  title="Click to expand / Double-click to edit name"
                                >
                                  {b.name}
                                  {app && <span className="ml-1.5 text-[10px] align-top px-1 py-0.5 bg-[#F5A623]/15 text-[#F5A623] rounded font-medium">FORM</span>}
                                  {sig && <span className="ml-1.5 text-[10px] align-top px-1 py-0.5 bg-purple-500/15 text-purple-400 rounded font-medium">SIG</span>}
                                  {b.interview?.interviewed === 'Yes' && (
                                    <span className={`ml-1.5 text-[10px] align-top px-1 py-0.5 rounded font-medium ${
                                      b.interview.status === 'Successful Payment' ? 'bg-green-500/15 text-green-400' :
                                      b.interview.status === 'Follow Up' ? 'bg-yellow-500/15 text-yellow-400' :
                                      b.interview.status === 'Not Ready' ? 'bg-red-500/15 text-red-400' :
                                      b.interview.status === 'Pending' ? 'bg-blue-500/15 text-blue-400' :
                                      'bg-white/10 text-gray-300'
                                    }`}>{b.interview.status || 'Interviewed'}</span>
                                  )}
                                </button>
                              )}
                              {bookedSlot && (
                                <div className="mt-1">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F5A623]/15 text-[#F5A623] rounded text-[10px] font-medium whitespace-nowrap">
                                    <Clock className="w-2.5 h-2.5" /> {bookedSlot.day_label}, {bookedSlot.time}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="py-3 pr-2 text-gray-300">{b.gender}</td>
                            <td className="py-3 pr-2 text-gray-300 text-xs truncate">{b.email}</td>
                            <td className="py-3 pr-2 text-gray-300 text-xs">
                              {editingPhone === (b.id || i) ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    data-testid={`phone-edit-input-${i}`}
                                    value={phoneDraft}
                                    onChange={(e) => setPhoneDraft(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') savePhone(b.id, phoneDraft); if (e.key === 'Escape') setEditingPhone(null); }}
                                    className="bg-[#1A1A1D] border border-white/20 text-white rounded px-2 py-1 text-sm w-36 focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50"
                                    autoFocus
                                  />
                                  <button onClick={() => savePhone(b.id, phoneDraft)} className="text-green-400 hover:text-green-300 text-xs">Save</button>
                                  <button onClick={() => setEditingPhone(null)} className="text-gray-500 hover:text-gray-300 text-xs">Esc</button>
                                </div>
                              ) : (
                                <span
                                  data-testid={`phone-cell-${i}`}
                                  className="cursor-pointer hover:text-[#F5A623] transition-colors"
                                  onClick={() => { setEditingPhone(b.id || i); setPhoneDraft(b.phone || ''); }}
                                  title="Click to edit"
                                >{b.phone}</span>
                              )}
                            </td>
                            <td className="py-3 pr-3 text-gray-400 whitespace-nowrap text-xs">{new Date(b.booked_at).toLocaleDateString()}</td>
                            <td className="py-3">
                              <div className="flex items-center gap-1 justify-center">
                                {[
                                  { key: 'message1', num: '1' },
                                  { key: 'message2', num: '2' },
                                  { key: 'message3', num: '3' },
                                  { key: 'message4', num: '4' }
                                ].map(({ key, num }) => {
                                  const sent = b[`wa${num}_sent`];
                                  return (
                                  <div key={key} className="flex flex-col items-center gap-0.5">
                                    <button
                                      data-testid={`wa-tick-${num}-${i}`}
                                      onClick={() => toggleWaSent(b, num)}
                                      className={`w-4 h-4 rounded border transition-colors flex items-center justify-center shrink-0 cursor-pointer ${
                                        sent ? 'bg-[#25D366] border-[#25D366]' : 'border-gray-600 hover:border-gray-400'
                                      }`}
                                      title={sent ? `Msg ${num} sent (click to unmark)` : `Mark msg ${num} as sent`}
                                    >
                                      {sent && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                                    </button>
                                    <button
                                      data-testid={`wa-send-${key}-${i}`}
                                      disabled={!waMessages[key]}
                                      onClick={() => openWhatsApp(b.phone, waMessages[key], selectedEvent, b, key)}
                                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                                        !waMessages[key]
                                          ? 'bg-white/5 text-gray-600 cursor-not-allowed'
                                          : sent
                                            ? 'bg-[#25D366] text-white cursor-pointer'
                                            : 'bg-[#25D366]/15 hover:bg-[#25D366]/30 text-[#25D366] cursor-pointer'
                                      }`}
                                      title={!waMessages[key] ? `Message ${num} not set` : sent ? `Resend message ${num}` : `Send message ${num}`}
                                    >
                                      <Send className="w-3 h-3" /> {num}
                                    </button>
                                  </div>
                                  );
                                })}
                              </div>
                            </td>
                            <td className="py-3 text-center">
                              {bookedSlot ? (
                                <button
                                  data-testid={`wa-remind-${i}`}
                                  onClick={() => sendReminder(b, bookedSlot)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors bg-orange-500/15 hover:bg-orange-500/30 text-orange-400 cursor-pointer"
                                  title={`Remind interviewer about ${b.name}'s session`}
                                >
                                  <Bell className="w-3 h-3" /> Remind
                                </button>
                              ) : (
                                <span className="text-gray-600 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-[#0A0A0B]">
                              <td colSpan={8} className="px-2 sm:px-6 py-4">
                                {/* Application Q&A */}
                                {app && (
                                  <div className="space-y-2 mb-5">
                                    <p className="text-[#F5A623] text-xs uppercase tracking-widest font-bold">Application Answers</p>
                                    {[
                                      { k: 'q1', q: 'What do you truly want, and why does it matter?' },
                                      { k: 'q2', q: 'What challenges are holding you back?' },
                                      { k: 'q3', q: 'Are you lacking of opportunity? Lacking of energy?' },
                                      { k: 'q4', q: 'What part of life do you need Master Steven to consult?' },
                                      { k: 'q5', q: 'Describe one of the issues that you want Master Steven to advice.' },
                                      { k: 'q6', q: 'If you get the advice and tools, would you follow? Why?' }
                                    ].map(({ k, q }) => (
                                      <div key={k}>
                                        <p className="text-gray-500 text-xs">{q}</p>
                                        <p className="text-gray-200 text-sm">{app[k] || '—'}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {!app && <p className="text-gray-500 text-xs mb-4 italic">No application form submitted.</p>}

                                {/* Signature */}
                                {sig ? (
                                  <div className="mb-5">
                                    <p className="text-purple-400 text-xs uppercase tracking-widest font-bold mb-2">Signature</p>
                                    <div className="inline-block bg-white rounded-lg p-2 border border-white/20">
                                      <img src={`${API_URL}/api/admin/signatures/${sig.id}?auth=${token}`} alt={`${b.name}'s signature`} className="max-h-32 max-w-xs object-contain" />
                                    </div>
                                    <p className="text-gray-500 text-xs mt-1">Uploaded by: {sig.name} — {new Date(sig.uploaded_at).toLocaleString()}</p>
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-xs mb-4 italic">No signature uploaded.</p>
                                )}

                                {/* Interview Form */}
                                {(() => {
                                  const iv = getInterview(b.id, b.interview);
                                  return (
                                    <div className="border-t border-white/10 pt-4">
                                      <p className="text-[#F5A623] text-xs uppercase tracking-widest font-bold mb-3">Interview Notes</p>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-3">
                                        <div className="space-y-1">
                                          <Label className="text-gray-400 text-xs">Interviewed?</Label>
                                          <select value={iv.interviewed} onChange={(e) => setInterviewField(b.id, 'interviewed', e.target.value, b.interview)} className="w-full h-9 bg-[#1A1A1D] border border-white/20 text-white rounded-md px-2 text-sm">
                                            <option value="">—</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                          </select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-gray-400 text-xs">Status</Label>
                                          <select value={iv.status} onChange={(e) => setInterviewField(b.id, 'status', e.target.value, b.interview)} className="w-full h-9 bg-[#1A1A1D] border border-white/20 text-white rounded-md px-2 text-sm">
                                            <option value="">—</option>
                                            <option value="Successful Payment">Successful Payment</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Follow Up">Follow Up</option>
                                            <option value="Not Ready">Not Ready</option>
                                          </select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-gray-400 text-xs">Program</Label>
                                          <select value={iv.program} onChange={(e) => setInterviewField(b.id, 'program', e.target.value, b.interview)} className="w-full h-9 bg-[#1A1A1D] border border-white/20 text-white rounded-md px-2 text-sm">
                                            <option value="">—</option>
                                            <option value="SOW (RM3888)">SOW (RM3888)</option>
                                            <option value="SOW (RM3688)">SOW (RM3688)</option>
                                            <option value="SOW (RM3388)">SOW (RM3388)</option>
                                            <option value="SOW (RM4688)">SOW (RM4688)</option>
                                            <option value="SOW (SGD1888)">SOW (SGD1888)</option>
                                            <option value="SOW (SGD1388)">SOW (SGD1388)</option>
                                            <option value="SOW (RM1588)">SOW (RM1588)</option>
                                            <option value="SOW (SGD1288)">SOW (SGD1288)</option>
                                            <option value="SOW (SGD2376)">SOW (SGD2376)</option>
                                            <option value="SOW (SGD2888) - EXPRESS">SOW (SGD2888) - EXPRESS</option>
                                            <option value="SOW (RM6888) - EXPRESS">SOW (RM6888) - EXPRESS</option>
                                            <option value="SOW (RM8888) - EXPRESS">SOW (RM8888) - EXPRESS</option>
                                            <option value="FREGOL">FREGOL</option>
                                            <option value="FREGOL - EXPRESS">FREGOL - EXPRESS</option>
                                          </select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-gray-400 text-xs">Payment Method</Label>
                                          <select value={iv.payment_method} onChange={(e) => setInterviewField(b.id, 'payment_method', e.target.value, b.interview)} className="w-full h-9 bg-[#1A1A1D] border border-white/20 text-white rounded-md px-2 text-sm">
                                            <option value="">—</option>
                                            <option value="Full Payment">Full Payment</option>
                                            <option value="Installment">Installment</option>
                                            <option value="Deposit">Deposit</option>
                                          </select>
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-gray-400 text-xs">Payment Amount</Label>
                                          <Input value={iv.payment_amount} onChange={(e) => setInterviewField(b.id, 'payment_amount', e.target.value, b.interview)} className="bg-[#1A1A1D] border-white/20 text-white h-9" placeholder="e.g. RM3888" />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-gray-400 text-xs">Language</Label>
                                          <select value={iv.language || ''} onChange={(e) => setInterviewField(b.id, 'language', e.target.value, b.interview)} className="w-full h-9 bg-[#1A1A1D] border border-white/20 text-white rounded-md px-2 text-sm">
                                            <option value="">—</option>
                                            <option value="English">English</option>
                                            <option value="Cantonese">Cantonese</option>
                                            <option value="Mandarin">Mandarin</option>
                                            <option value="Malay">Malay</option>
                                          </select>
                                        </div>
                                        <div className="space-y-1 flex items-end">
                                          <Button onClick={() => saveInterview(b.id)} disabled={interviewSaving || !interviewData[b.id]} className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold h-9 w-full text-sm">
                                            {interviewSaved === b.id ? 'Saved!' : 'Save'}
                                          </Button>
                                        </div>
                                      </div>
                                      {/* Payment Gateway & Transaction Fee Calculator */}
                                      {iv.payment_amount && (() => {
                                        const GATEWAYS = [
                                          { label: 'MY Direct Bank Transfer', fee: 0 },
                                          { label: 'SG Direct Bank Transfer', fee: 0 },
                                          { label: 'SG Paynow', fee: 0 },
                                          { label: 'Billplz FPX', fee: 0 },
                                          { label: 'Billplz Senangpay', fee: 1.8 },
                                          { label: '2C2P', fee: 2.5 },
                                          { label: 'Stripe', fee: 3.5 },
                                          { label: 'Stripe SG', fee: 3.5 },
                                          { label: 'Senangpay (Card)', fee: 2.5 },
                                          { label: 'Senangpay (FPX)', fee: 1.5 },
                                          { label: 'CHIP FPX', fee: 0 },
                                          { label: 'CHIP Credit Card (Local)', fee: 1.8 },
                                          { label: 'CHIP Debit Card (Local)', fee: 1.0 },
                                          { label: 'CHIP Credit Card (Int\'l)', fee: 2.5 },
                                          { label: 'CHIP Debit Card (Int\'l)', fee: 1.5 },
                                          { label: 'Payex RHB 6 Months', fee: 4.0 },
                                          { label: 'Payex RHB 12 Months', fee: 5.0 },
                                          { label: 'Payex RHB 24 Months', fee: 7.5 },
                                          { label: 'Payex RHB 36 Months', fee: 8.0 },
                                          { label: 'Payex Ambank 6 Months', fee: 4.0 },
                                          { label: 'Payex Ambank 12 Months', fee: 4.5 },
                                          { label: 'Payex Ambank 24 Months', fee: 7.0 },
                                          { label: 'Payex Ambank 36 Months', fee: 8.0 },
                                          { label: 'Payex Ambank 48 Months', fee: 8.5 },
                                          { label: 'Payex Ambank 60 Months', fee: 9.0 },
                                          { label: 'Payex PBB 6 Months', fee: 5.0 },
                                          { label: 'Payex PBB 12 Months', fee: 6.0 },
                                          { label: 'Payex PBB 24 Months', fee: 7.0 },
                                          { label: 'Payex PBB 36 Months', fee: 7.5 },
                                          { label: 'Payex OCBC 6 Months', fee: 4.0 },
                                          { label: 'Payex OCBC 12 Months', fee: 5.0 },
                                          { label: 'Payex OCBC 24 Months', fee: 7.0 },
                                          { label: 'Payex OCBC 36 Months', fee: 8.0 },
                                          { label: 'Payex CIMB 6 Months', fee: 6.0 },
                                          { label: 'Payex CIMB 12 Months', fee: 6.5 },
                                          { label: 'Payex CIMB 24 Months', fee: 8.0 },
                                          { label: 'Payex CIMB 36 Months', fee: 8.5 },
                                          { label: 'Payex CIMB 48 Months', fee: 9.5 },
                                          { label: 'Payex CIMB 60 Months', fee: 12.0 },
                                          { label: 'Payex UOB 6 Months', fee: 4.0 },
                                          { label: 'Payex UOB 12 Months', fee: 5.0 },
                                          { label: 'Payex UOB 24 Months', fee: 6.5 },
                                          { label: 'Payex UOB 36 Months', fee: 7.0 },
                                          { label: 'Payex Maybank 6 Months', fee: 4.5 },
                                          { label: 'Payex Maybank 12 Months', fee: 5.2 },
                                          { label: 'Payex Maybank 18 Months', fee: 6.5 },
                                          { label: 'Payex Maybank 24 Months', fee: 8.0 },
                                          { label: 'Payex Maybank 36 Months', fee: 8.5 },
                                          { label: 'Payex Affin 6 Months', fee: 3.8 },
                                          { label: 'Payex Affin 12 Months', fee: 5.0 },
                                          { label: 'Payex Affin 24 Months', fee: 6.0 },
                                          { label: 'Payex Affin 36 Months', fee: 7.5 },
                                          { label: 'Payex HLB 6 Months', fee: 6.0 },
                                          { label: 'Payex HLB 12 Months', fee: 6.5 },
                                          { label: 'Payex HLB 24 Months', fee: 7.0 },
                                          { label: 'Payex HLB 36 Months', fee: 7.5 },
                                          { label: 'Payex HLB 48 Months', fee: 9.0 },
                                          { label: 'Payex HLB 60 Months', fee: 10.0 },
                                          { label: 'Payex HSBC 6 Months', fee: 5.0 },
                                          { label: 'Payex HSBC 12 Months', fee: 6.0 },
                                          { label: 'Payex HSBC 24 Months', fee: 7.0 },
                                          { label: 'Payex HSBC 36 Months', fee: 8.0 },
                                          { label: 'Payex Std Chart 6 Months', fee: 5.0 },
                                          { label: 'Payex Std Chart 12 Months', fee: 6.0 },
                                          { label: 'Payex Std Chart 24 Months', fee: 7.0 },
                                          { label: 'Payex Std Chart 36 Months', fee: 8.0 },
                                        ];
                                        const amtStr = iv.payment_amount.replace(/,/g, '');
                                        const nums = amtStr.match(/[\d.]+/);
                                        const amount = nums ? parseFloat(nums[0]) : 0;
                                        const selectedGw = iv.gateway || '';
                                        const gw = GATEWAYS.find(g => g.label === selectedGw);
                                        const feePercent = gw ? gw.fee : 0;
                                        const feeAmount = amount * (feePercent / 100);
                                        const finalAmount = amount - feeAmount;
                                        return (
                                          <div className="mt-3 p-3 bg-[#0A0A0B] border border-white/10 rounded-lg">
                                            <Label className="text-gray-400 text-xs">Payment Gateway & Transaction Fee</Label>
                                            <select
                                              value={selectedGw}
                                              onChange={(e) => setInterviewField(b.id, 'gateway', e.target.value, b.interview)}
                                              className="w-full h-9 mt-1 bg-[#1A1A1D] border border-white/20 text-white rounded-md px-2 text-sm"
                                            >
                                              <option value="">Select gateway...</option>
                                              <optgroup label="Direct Transfer">
                                                {GATEWAYS.filter(g => g.label.includes('Direct') || g.label.includes('Paynow')).map(g => (
                                                  <option key={g.label} value={g.label}>DDC - {g.label} ({g.fee}%)</option>
                                                ))}
                                              </optgroup>
                                              <optgroup label="Online Payment">
                                                {GATEWAYS.filter(g => !g.label.includes('Direct') && !g.label.includes('Paynow') && !g.label.includes('Payex') && !g.label.includes('CHIP')).map(g => (
                                                  <option key={g.label} value={g.label}>DDC - {g.label} ({g.fee}%)</option>
                                                ))}
                                              </optgroup>
                                              <optgroup label="CHIP">
                                                {GATEWAYS.filter(g => g.label.includes('CHIP')).map(g => (
                                                  <option key={g.label} value={g.label}>DDC - {g.label} ({g.fee}%)</option>
                                                ))}
                                              </optgroup>
                                              <optgroup label="Payex Installment">
                                                {GATEWAYS.filter(g => g.label.includes('Payex')).map(g => (
                                                  <option key={g.label} value={g.label}>DDC - {g.label} ({g.fee}%)</option>
                                                ))}
                                              </optgroup>
                                            </select>
                                            {selectedGw && amount > 0 && (
                                              <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                                                <div>
                                                  <p className="text-gray-500 text-[10px]">Payment Amount</p>
                                                  <p className="text-white font-bold text-sm">{iv.payment_amount}</p>
                                                </div>
                                                <div>
                                                  <p className="text-gray-500 text-[10px]">Transaction Fee ({feePercent}%)</p>
                                                  <p className="text-red-400 font-bold text-sm">- {amtStr.includes('SGD') ? 'SGD' : 'RM'}{feeAmount.toFixed(2)}</p>
                                                </div>
                                                <div>
                                                  <p className="text-gray-500 text-[10px]">Final Amount</p>
                                                  <p className="text-green-400 font-bold text-sm">{amtStr.includes('SGD') ? 'SGD' : 'RM'}{finalAmount.toFixed(2)}</p>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      <div className="space-y-1">
                                        <Label className="text-gray-400 text-xs">Notes / Remark</Label>
                                        <textarea value={iv.notes} onChange={(e) => setInterviewField(b.id, 'notes', e.target.value, b.interview)} rows={10} className="w-full bg-[#1A1A1D] border border-white/20 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#F5A623]/50 resize-y" placeholder="Interviewer remarks..." />
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

        {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Import Sheet */}
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
              <FileSpreadsheet className="w-5 h-5 text-[#F5A623]" /> Import Google Sheet
            </h2>
            <p className="text-gray-400 text-sm mb-3">Paste a public Google Sheet link. The sheet must be set to "Anyone with the link can view".</p>
            <div className="flex gap-3">
              <Input
                data-testid="sheet-url-input"
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                className="bg-[#0A0A0B] border-white/20 text-white flex-1"
                placeholder="https://docs.google.com/spreadsheets/d/..."
              />
              <Button
                data-testid="sheet-import-btn"
                onClick={importSheet}
                disabled={importingSheet || !sheetUrl.trim()}
                className="bg-[#F5A623] hover:bg-[#E59400] text-black font-bold shrink-0"
              >
                {importingSheet ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>

          {/* Aggregate Overview */}
          {aggregate && aggregate.total_sheets > 0 && (
            <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-[#F5A623]" /> Overall Performance
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-[#0A0A0B] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-400 text-xs mb-1">Sheets Imported</p>
                  <p className="text-2xl font-bold text-white">{aggregate.total_sheets}</p>
                </div>
                <div className="bg-[#0A0A0B] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-400 text-xs mb-1">Total Consultations</p>
                  <p className="text-2xl font-bold text-white">{aggregate.total_consultations}</p>
                </div>
                <div className="bg-[#0A0A0B] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-400 text-xs mb-1">Successful Payments</p>
                  <p className="text-2xl font-bold text-green-400">{aggregate.total_successful}</p>
                </div>
                <div className="bg-[#0A0A0B] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-400 text-xs mb-1">Closing Rate</p>
                  <p className={`text-2xl font-bold ${aggregate.overall_closing_rate >= 50 ? 'text-green-400' : aggregate.overall_closing_rate >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>{aggregate.overall_closing_rate}%</p>
                </div>
                <div className="bg-[#0A0A0B] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-400 text-xs mb-1">RM Collection</p>
                  <p className="text-2xl font-bold text-[#F5A623]">RM{aggregate.total_rm.toLocaleString()}</p>
                </div>
                <div className="bg-[#0A0A0B] rounded-lg p-4 border border-white/5">
                  <p className="text-gray-400 text-xs mb-1">SGD Collection</p>
                  <p className="text-2xl font-bold text-[#F5A623]">SGD{aggregate.total_sgd.toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Imported Sheets List */}
          {importedSheets.length > 0 && (
            <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
                <FileSpreadsheet className="w-5 h-5 text-[#F5A623]" /> Imported Sheets ({importedSheets.length})
              </h2>
              <div className="space-y-3">
                {importedSheets.map((sheet) => {
                  const a = sheet.analytics || {};
                  const isExpanded = expandedSheet === sheet.id;
                  const details = sheetDetails[sheet.id];
                  return (
                    <div key={sheet.id} className="bg-[#0A0A0B] border border-white/5 rounded-lg overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="text-white font-bold">{sheet.webinar_date || 'Unknown Date'}</h3>
                              {sheet.webinar_title && <span className="text-gray-500 text-xs truncate">{sheet.webinar_title}</span>}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                              <span className="text-gray-400">{a.total_consultations} consultations</span>
                              <span className="text-green-400">{a.successful_payments} successful</span>
                              <span className="text-yellow-400">{a.pending_payments} pending</span>
                              <span className={`font-medium ${a.closing_rate >= 50 ? 'text-green-400' : a.closing_rate >= 30 ? 'text-yellow-400' : 'text-red-400'}`}>{a.closing_rate}% close rate</span>
                              {a.rm_collection > 0 && <span className="text-[#F5A623]">RM{a.rm_collection.toLocaleString()}</span>}
                              {a.sgd_collection > 0 && <span className="text-[#F5A623]">SGD{a.sgd_collection.toLocaleString()}</span>}
                            </div>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => viewSheetDetails(sheet.id)} className="text-gray-400 hover:text-white">
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteSheet(sheet.id)} className="text-gray-400 hover:text-red-400">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      {isExpanded && details && (
                        <div className="border-t border-white/5 p-4 space-y-4">
                          <div>
                            <p className="text-[#F5A623] text-xs uppercase tracking-widest font-bold mb-2">Consultations</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-left text-sm">
                                <thead>
                                  <tr className="border-b border-white/10 text-gray-400">
                                    <th className="pb-2 pr-3">#</th>
                                    <th className="pb-2 pr-3">Name</th>
                                    <th className="pb-2 pr-3">Date</th>
                                    <th className="pb-2 pr-3">Status</th>
                                    <th className="pb-2 pr-3">Program</th>
                                    <th className="pb-2">Payment</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(details.consultations || []).map((c, ci) => (
                                    <tr key={ci} className="border-b border-white/5">
                                      <td className="py-2 pr-3 text-gray-500">{ci + 1}</td>
                                      <td className="py-2 pr-3 text-white">{c.name}</td>
                                      <td className="py-2 pr-3 text-gray-400">{c.date}</td>
                                      <td className="py-2 pr-3">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          c.status.toLowerCase().includes('successful') ? 'bg-green-500/15 text-green-400' :
                                          c.status.toLowerCase().includes('pending') ? 'bg-yellow-500/15 text-yellow-400' :
                                          c.status.toLowerCase().includes('follow') ? 'bg-blue-500/15 text-blue-400' :
                                          'bg-white/10 text-gray-300'
                                        }`}>{c.status || '\u2014'}</span>
                                      </td>
                                      <td className="py-2 pr-3 text-gray-300 text-xs">{c.program || '\u2014'}</td>
                                      <td className="py-2 text-[#F5A623] font-medium">{c.payment_amount || '\u2014'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {details.applications && details.applications.length > 0 && (
                            <div>
                              <p className="text-[#F5A623] text-xs uppercase tracking-widest font-bold mb-2">Application Answers ({details.applications.length})</p>
                              <div className="space-y-3">
                                {details.applications.map((app, ai) => (
                                  <div key={ai} className="bg-[#1A1A1D] rounded-lg p-3 border border-white/5">
                                    <p className="text-white font-medium text-sm mb-2">{app.name} <span className="text-gray-500 font-normal text-xs ml-2">{app.phone}</span></p>
                                    <div className="space-y-1.5">
                                      {(details.questions || []).map((q, qi) => (
                                        <div key={qi}>
                                          <p className="text-gray-500 text-xs">{q}</p>
                                          <p className="text-gray-200 text-sm">{app.answers?.[`q${qi+1}`] || '\u2014'}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Applications Across Sheets */}
          {aggregate && aggregate.all_applications && aggregate.all_applications.length > 0 && (
            <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
              <button
                onClick={() => setExpandedApps(!expandedApps)}
                className="w-full flex items-center justify-between"
              >
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#F5A623]" /> All Client Applications ({aggregate.all_applications.length})
                </h2>
                {expandedApps ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
              </button>
              {expandedApps && (
                <div className="mt-4 space-y-3">
                  {aggregate.all_applications.map((app, ai) => (
                    <div key={ai} className="bg-[#0A0A0B] rounded-lg p-3 border border-white/5">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="text-white font-medium text-sm">{app.name}</p>
                        <span className="text-gray-500 text-xs">{app.phone}</span>
                        {app.webinar_date && <span className="text-[#F5A623] text-xs bg-[#F5A623]/10 px-2 py-0.5 rounded">{app.webinar_date}</span>}
                      </div>
                      <div className="space-y-1.5">
                        {(aggregate.questions || []).map((q, qi) => (
                          <div key={qi}>
                            <p className="text-gray-500 text-xs">{q}</p>
                            <p className="text-gray-200 text-sm">{app.answers?.[`q${qi+1}`] || '\u2014'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {activeTab === 'signature' && (
        <div className="space-y-6">
          <div className="bg-[#1A1A1D] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold flex items-center gap-2" data-testid="sig-pending-title">
                <FileSignature className="w-5 h-5 text-[#F5A623]" /> Signature Pending List
                {sigPendingTotal > 0 && <span className="text-sm font-normal text-gray-400 ml-1">({sigPendingTotal})</span>}
              </h2>
              <Button onClick={fetchSigPending} variant="ghost" className="text-gray-400 hover:text-white text-xs h-8" data-testid="sig-pending-refresh">
                Refresh
              </Button>
            </div>
            {sigPending.length === 0 ? (
              <div className="text-center py-12">
                <FileSignature className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No pending signatures</p>
                <p className="text-gray-600 text-xs mt-1">Clients with "Successful Payment" will appear here automatically.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {sigPending.map((item, i) => {
                  const emoji = item.is_express ? '\ud83d\udd25' : (item.due_date && (() => {
                    try {
                      const parts = item.due_date.split(' ');
                      const monthMap = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
                      const d = parseInt(parts[0]);
                      const m = monthMap[parts[1]];
                      const now = new Date();
                      const due = new Date(now.getFullYear(), m, d);
                      if (due < now) due.setFullYear(due.getFullYear() + 1);
                      const diff = (due - now) / (1000*60*60*24);
                      return diff <= 14 ? '\u2757\ufe0f' : '';
                    } catch { return ''; }
                  })());
                  const displayDate = item.is_express ? 'Express' : (item.due_date || '—');
                  const langStr = item.language ? ` (${item.language})` : ' (No language set)';
                  const pendingStr = item.is_pending_payment ? ' *PENDING PAYMENT' : '';
                  return (
                    <div key={item.id || i} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-colors ${
                      item.is_express ? 'bg-red-500/8 border-red-500/20' : 
                      emoji === '\u2757\ufe0f' ? 'bg-orange-500/8 border-orange-500/20' : 
                      'bg-[#0A0A0B] border-white/5'
                    }`} data-testid={`sig-pending-item-${i}`}>
                      <span className="text-lg shrink-0 w-6 text-center">{emoji}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-white font-medium text-sm cursor-pointer hover:text-[#F5A623] hover:underline transition-colors" onClick={() => goToBooking(item.event_slug, item.id)}>{item.name}</span>
                        {item.short_code && <span className="text-gray-400 text-sm ml-1">({item.short_code})</span>}
                        <span className="text-gray-500 text-sm"> - </span>
                        <span className={`text-sm ${item.is_express ? 'text-red-400 font-bold' : 'text-gray-300'}`}>{displayDate}</span>
                        <span className={`text-sm ${item.language ? 'text-gray-400' : 'text-yellow-500/60 italic'}`}>{langStr}</span>
                        {pendingStr && <span className="text-red-400 text-xs font-bold ml-2">{pendingStr}</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-gray-600 text-xs">{item.event_name}</span>
                        <button
                          onClick={() => markSigDone(item.id)}
                          className="px-2.5 py-1 bg-green-500/15 text-green-400 rounded text-xs hover:bg-green-500/30 font-medium transition-colors"
                          data-testid={`sig-done-btn-${i}`}
                        >Done</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        )}

      </div>
    </div>
  );
}
